import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
    Drawer,
    Button,
    Space,
    Form,
    Row,
    Col,
    Input,
    Select,
    DatePicker,
    Tooltip,
    Spin,
    Typography,
    Radio,
    Collapse,
    Modal,
    message,
    Tag,
    Table,
} from "antd";
import { InfoCircleOutlined, MinusCircleOutlined, PlusOutlined, CopyOutlined } from "@ant-design/icons";
import { useMsal } from "@azure/msal-react";
import { useDebouncedCallback } from "use-debounce";
import moment from "moment";
import { ClinicalStudy, IDrawerComponentProps, FormValues, Group, IOption, Owner, Projects, IProcessedGroups, IEditSpacePayload, ICreateSpacePayload } from "./types";
import { licensingObligation, profileOptionsMap, retentionTerms, skeletonData, retentionTermsExtended, DO_NOT_EDIT_KEY, DO_NOT_EDIT_MESSAGE, commonProject, commonStudy, GXP_VALIDATED_DOMAINS, rdsTypeOptions, RDS_TYPE_DEFAULT, idComplianceOptions, isGxpSpaceType, DEFAULT_CLASSIFICATIONS, classificationsForPrimaryDomain } from "./constants";
import { fetchGroups, fetchRegionData, fetchUsers, getProjectsAndStudies, grantPrivileges, validateSpaceName, validate_git_link, fetchGroupMembers, fetchTrainingDataByIds, fetchParametersSecrets, get_git_list } from "./drawerApi";
import { USER_GUIDE_URL, MAX_SUM_OF_OWNERS_AND_GROUPS, RETENTION_DEFAULT_EXPERIMENT, ENVIRONMENT, BYOC_SETUP_GUIDE_URL, GROUP_FOR_HPC_PROJECT_CLAIM } from "const";
import { encryptString, decryptString } from "utils/encrypt";
import { useParams } from "react-router-dom";
import "./style.scss";
import FormSkeleton from "common/skeleton";
import SelectionModal from "components/SelectionModal";
import type { ColumnsType } from "antd/lib/table";
import { getSagemakerDomainByDisplayName, safeParseArray } from "utils/utils";
import { hostnameUrl } from "http/index";
import { gpuInstances } from "./constants";

console.log("Environment variable in Drawer component:", ENVIRONMENT);

const { Option } = Select;
const { Text } = Typography;
interface MsalAccount {
    username: string;
    [key: string]: unknown;
}
const contentStyle: React.CSSProperties = {
    padding: 50,
    borderRadius: 4,
};
// Types (same as in SelectionPopup)
interface ProjectItem {
    project_id: string;
    project_name: string;
}

interface ClinicalStudyItem {
    study_id: string;
    study_name: string;
    project_id: string;
    project_name: string;
}
type SelectionType = "project" | "study";

type ItemType = Projects | ClinicalStudy;
const DrawerComponent: React.FC<IDrawerComponentProps> = ({
    openMetadataForm = false,
    onClose = () => { },
    editData = undefined, // Defaults to undefined (for create space)
    mode,
    onSuccess,
    loading,
    domainName,
    sagemakerDomain,
    bucketTechName,
    parentTemplateRepo,
    galleryProfileType,
}) => {

    const isExtDataExchange = domainName === "External Data Exchange";

    const [form] = Form.useForm();
    const [owners, setOwners] = useState<Owner[]>([]);
    const [groups, setGroups] = useState<Group[]>([{ group_name: "", access_level: "ro" }]);
    const oldGroupsRef = useRef<Group[] | null>(null);
    // Tracks whether the requested_for field has an owner-group validation error.
    // Using a ref (not state) so the validator closure always reads the latest value
    // without requiring extra re-renders.
    const requestorErrorRef = useRef<boolean>(false);
    const requestorErrorMessageRef = useRef<string>("");
    // Always-current profile type for validators called from setTimeout (avoids stale closure)
    const profileTypeForValidatorRef = useRef<string>("");
    const [projects, setProjects] = useState<Projects[]>([]);
    const [filteredProjects, setFilteredProjects] = useState<{ project_id: string; project_name: string }[]>([]); // shown in dropdown
    const [clinicalStudy, setClinicalStudy] = useState<ClinicalStudy[]>([]);
    const [filteredStudies, setFilteredStudies] = useState<{ study_id: string; study_name: string }[]>([]);
    const [retentionTimeType, setRetentionTimeType] = useState<"period" | "date" | "experimentperiod">(RETENTION_DEFAULT_EXPERIMENT == "true" ? "experimentperiod" : "period"); // To track the selection of retention time type
    const [domainsList, setDomainsList] = useState<{ display_name: string, disabled: boolean }[]>([]);
    const [regionsList, setRegionsList] = useState<string[]>([]);
    const [selectedPrimaryDomain, setSelectedPrimaryDomain] = useState<string>("");
    const [secondaryDomain, setSecondaryDomain] = useState<string[]>([]);
    const [groupOptions, setGroupOptions] = useState<IOption[]>([]);
    const [fetchingGroups, setFetchingGroups] = useState<boolean>(false);
    const [ownerOptions, setOwnerOptions] = useState<IOption[]>([]);
    const [fetchingOwners, setFetchingOwners] = useState<boolean>(false);
    const [projectType, setProjectType] = useState<string>("");
    const [profileType, setProfileType] = useState<string>("");
    const [bucketType, setBucketType] = useState<string>("");
    const [isNotSpaceOwner, setIsNotSpaceOwner] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [selectedType, setSelectedType] = useState<SelectionType | null>(null);
    const [selectedItems, setSelectedItems] = useState<ItemType[]>([]);
    const [selectedProject, setSelectedProject] = useState<string | null>(null);
    const [selectedStudy, setSelectedStudy] = useState<string | null>(null);
    const [spaceNameCount, setSpaceNameCount] = useState(0);
    const [showGxpWarningModal, setShowGxpWarningModal] = useState<boolean>(false);
    const [pendingBucketType, setPendingBucketType] = useState<string>("");
    const [gxpWarningTrigger, setGxpWarningTrigger] = useState<"bucketType" | "domain" | null>(null);
    const needRdsStorage = (Form.useWatch("need_rds_storage", form) as string) ?? "";
    const [isGitLink, setisGitLink] = useState(false);
    const [isGitRepoNeeded, setIsGitRepoNeeded] = useState<boolean>(true);
    const [gpuModalVisible, setGpuModalVisible] = useState(false);
    const [gpuModalContent, setGpuModalContent] = useState("");
    const [loadingTrainingGroups, setLoadingTrainingGroups] = useState<boolean>(false);
    const [parametersModalVisible, setParametersModalVisible] = useState(false);
    const [parameters, setParameters] = useState<Array<{ name: string; value: string; path?: string }>>([]);
    const [secrets, setSecrets] = useState<Array<{ name: string; value: string; arn?: string }>>([]);
    const [originalParameters, setOriginalParameters] = useState<Array<{ name: string; value: string; path?: string }>>([]);
    const [originalSecrets, setOriginalSecrets] = useState<Array<{ name: string; value: string; arn?: string }>>([]);
    const [loadingParametersSecrets, setLoadingParametersSecrets] = useState(false);
    const [availableClassifications, setAvailableClassifications] = useState<string[]>(() =>
        classificationsForPrimaryDomain(domainName, DEFAULT_CLASSIFICATIONS),
    );
    const msalInstance = useMsal();
    const [GitList, setGitList] = useState<string[]>([]);
    const params = useParams();

    const maxOwners = MAX_SUM_OF_OWNERS_AND_GROUPS;
    const MAX_OWNERS_GROUPS = parseInt(maxOwners);
    // Keep ref in sync on every render so deferred validators always read the latest value
    profileTypeForValidatorRef.current = profileType;

    /**
     * Check if a domain has validated GxP processes
     * @param domainName - The domain name to check
     * @returns boolean - True if domain has validated GxP processes
     */
    const isDomainGxpValidated = (domainName: string): boolean => {
        return GXP_VALIDATED_DOMAINS.includes(domainName);
    };

    /**
     * Check if we should enforce Graph API (owner-group restriction)
     * ONLY applies to GxP spaces - non-GxP spaces are always unrestricted
     * @param bucketType - The bucket type (gxp or nongxp)
     * @param domainName - The domain name
     * @returns boolean - True if bucketType is "gxp" AND domain has owner-group array with at least one valid group
     */
    const shouldEnforceGraphApi = (bucketType: string, domainName: string): boolean => {
        if (!domainName || bucketType?.toLowerCase() !== "gxp") return false;

        try {
            const domainsData: any[] = JSON.parse(localStorage.getItem("domainsData") || "[]");
            const domain = domainsData.find(d => d.display_name === domainName);
            const ownerGroups = domain?.owner_group || [];
            return Array.isArray(ownerGroups) && ownerGroups.length > 0 && ownerGroups.some((g: any) => g?.id);
        } catch (err) {
            console.error("Error checking owner-group:", err);
            return false;
        }
    };

    /**
     * Validate if a user (by email) is a member of any of the admin groups
     * @param groupIds - Array of admin group IDs to check
     * @param userEmail - The user's email to check
     * @returns Promise<boolean> - True if user is in any of the groups
     */
    const validateUserInAdminGroups = async (groupIds: string[], userEmail: string): Promise<boolean> => {
        if (!userEmail || !groupIds || groupIds.length === 0) return false;

        try {
            // Fetch group members from all groups with the user's email as search term
            const members = await fetchGroupMembers(msalInstance, groupIds, userEmail);

            // Check if any member's email matches (case-insensitive)
            return members.some(member =>
                member.mail?.toLowerCase() === userEmail.toLowerCase(),
            );
        } catch (error) {
            console.error("Error validating user in admin groups:", error);
            return false; // If validation fails, assume user is NOT in group (safe default)
        }
    };

    /**
     * Validate and clear requestor/owners if owner group enforcement is required
     * @param domainName - The domain name to check
     * @param errorMessagePrefix - The prefix for the error message to display if requestor is not in owner group
     */
    const validateAndClearForAdminGroup = async (domainName: string, errorMessagePrefix: string, clearOwners = true): Promise<void> => {
        try {
            const domainsData: any[] = JSON.parse(localStorage.getItem("domainsData") || "[]");
            const domain = domainsData.find(d => d.display_name === domainName);
            const ownerGroups = domain?.owner_group || [];
            const groupIds: string[] = ownerGroups
                .map((g: any) => g?.id as unknown)
                .filter((id: unknown): id is string => typeof id === "string" && id.length > 0);

            if (groupIds.length > 0) {
                const currentRequestor = form.getFieldValue("requested_for");

                // Clear owners array only when context changes (domain/bucketType switch),
                // not when the user simply selects a new requestor
                if (clearOwners) {
                    setOwners([]);
                    form.setFieldsValue({ owners: [] });
                }

                // Validate requestor asynchronously
                if (currentRequestor) {
                    const isInGroup = await validateUserInAdminGroups(groupIds, currentRequestor);

                    if (!isInGroup) {
                        // Build error message with entitlement names and help documentation link
                        const entitlementNames: string = ownerGroups
                            .filter((g: { id?: string; name?: string }) => g?.name)
                            .map((g: { id?: string; name?: string }) => g.name as string)
                            .join(", ");

                        const errorMessage = (
                            <span>
                                {errorMessagePrefix}<strong>{entitlementNames}</strong>.{" "}
                                <a href={`${USER_GUIDE_URL}#owner-group-validation-for-restricted-domains`} target="_blank" rel="noopener noreferrer" className="help-link">
                                    <InfoCircleOutlined style={{ marginRight: 4 }} />
                                    Learn more
                                </a>
                            </span>
                        );

                        // Show validation error on the field
                        requestorErrorRef.current = true;
                        requestorErrorMessageRef.current = entitlementNames;
                        form.setFields([
                            {
                                name: "requested_for",
                                errors: [errorMessage as unknown as string],
                            },
                        ]);
                    } else {
                        requestorErrorRef.current = false;
                        requestorErrorMessageRef.current = "";
                    }
                    // If isInGroup === true, keep the requestor value
                }
            }
        } catch (error) {
            console.error("Error in validateAndClearForAdminGroup:", error);
        }
    };

    /**
     * Handle bucket type change with GxP validation
     * @param value - The selected bucket type (gxp or nongxp)
     */
    const handleBucketTypeChange = async (value: string) => {
        const currentDomain = form.getFieldValue("primaryDomain") || selectedPrimaryDomain;

        // Check if we need to enforce owner-group (only for GxP)
        const shouldEnforce = shouldEnforceGraphApi(value, currentDomain);

        if (shouldEnforce) {
            await validateAndClearForAdminGroup(
                currentDomain,
                "This user is not a member of the domain's owner group: ",
            );
        } else if (value?.toLowerCase() === "nongxp") {
            // Clear any existing validation errors when switching to non-GxP
            requestorErrorRef.current = false;
            form.setFields([
                {
                    name: "requested_for",
                    errors: [],
                },
            ]);
            // Clear training groups as they're only for GxP
            setGroupOptions([]);
            setLoadingTrainingGroups(false);
        }

        if (value === "gxp" && !isDomainGxpValidated(currentDomain)) {
            // Show warning modal for non-validated domains
            setPendingBucketType(value);
            setGxpWarningTrigger("bucketType"); // Track this was triggered by bucket type selection
            setShowGxpWarningModal(true);
            // Clear any requestor validation errors
            requestorErrorRef.current = false;
            form.setFields([
                {
                    name: "requested_for",
                    errors: [],
                },
            ]);
        } else {
            // Directly set the bucket type
            setBucketType(value);
            form.setFieldsValue({
                bucketType: value,
                ...(isGxpSpaceType(value) ? { fileSystem: undefined } : {}),
            });

            // Auto-select retention term based on bucket type
            if (value === "gxp") {
                // For GxP, default to Clinical study (25 years) 
                setRetentionTimeType("period");
                form.setFieldsValue({
                    retentionType: "period",
                    retentionTerm: "gxp_long",
                    retentionDate: undefined,
                });
            } else if (value === "nongxp") {
                // For non-GxP, default back to experiment period or short term based on environment
                const defaultType = RETENTION_DEFAULT_EXPERIMENT == "true" ? "experimentperiod" : "period";
                const defaultTerm = RETENTION_DEFAULT_EXPERIMENT == "true"
                    ? retentionTermsExtended[0]?.value || "two_weeks"
                    : "short";

                setRetentionTimeType(defaultType);
                form.setFieldsValue({
                    retentionType: defaultType,
                    retentionTerm: defaultTerm,
                    retentionDate: undefined,
                });
            }
        }
    };

    const handleExistingLink = (value: string) => {

        if (value === "") {
            setisGitLink(true);
        } else {
            setisGitLink(false);
            form.setFieldValue("customGitLink", null);
        }
    };

    const getGitList = async () => {
        const temp = await get_git_list(msalInstance);
        setGitList(temp);
    };

    /**
     * Handle GxP warning modal confirmation
     */
    const handleGxpWarningConfirm = () => {
        setBucketType(pendingBucketType);
        form.setFieldsValue({
            bucketType: pendingBucketType,
            ...(isGxpSpaceType(pendingBucketType) ? { fileSystem: undefined } : {}),
        });
        setShowGxpWarningModal(false);
        setPendingBucketType("");
        setGxpWarningTrigger(null);
        setSelectedPrimaryDomain(form.getFieldValue("primaryDomain") || selectedPrimaryDomain);
    };

    /**
     * Handle GxP warning modal cancellation
     */
    const handleGxpWarningCancel = () => {
        setShowGxpWarningModal(false);

        // Use the explicit trigger to determine what to clear
        if (gxpWarningTrigger === "domain") {
            // This was triggered by domain change (user had GxP selected and changed domain)
            // Clear the domain selection
            form.setFieldsValue({ primaryDomain: "" });
            setSelectedPrimaryDomain("");
        } else if (gxpWarningTrigger === "bucketType") {
            // This was triggered by bucket type selection (user selected GxP on non-validated domain)
            // Clear the bucket type selection
            form.setFieldsValue({ bucketType: "" });
            setBucketType("");
        }

        setPendingBucketType("");
        setGxpWarningTrigger(null);
    };

    /**
     * Get training IDs for a given domain from localStorage
     * @param domainName - The domain name to retrieve training IDs for
     * @returns string[] - Array of training IDs (empty if none or on error)
     */
    const getTrainingIdsForDomain = useCallback((domainName?: string): string[] => {
        if (!domainName) return [];
        try {
            const domainsData: any[] = JSON.parse(localStorage.getItem("domainsData") || "[]");
            const domain = domainsData.find(d => d.display_name === domainName);
            const trainingIds = domain?.training_ids || [];
            return Array.isArray(trainingIds) ? (trainingIds as string[]) : [];
        } catch (error) {
            console.error("Error retrieving training IDs:", error);
            return [];
        }
    }, []);

    /**
     * Check if a domain has training IDs configured
     * @param domainName - The domain name to check
     * @returns boolean - True if domain has training IDs array with at least one ID
     */
    const hasTrainingIdsConfigured = useCallback((domainName?: string): boolean => {
        return getTrainingIdsForDomain(domainName).length > 0;
    }, [getTrainingIdsForDomain]);

    // Get training IDs for the selected domain
    const trainingIdsForDomain = useMemo(() => {
        return getTrainingIdsForDomain(selectedPrimaryDomain);
    }, [selectedPrimaryDomain, getTrainingIdsForDomain]);

    /**
     * Fetch training-based groups for a domain
     * ONLY applies to GxP spaces - training groups are not available for non-GxP
     * @param domainName - The domain name to fetch training groups for
     */
    const fetchTrainingBasedGroups = async (domainName: string) => {
        if (!domainName) {
            setGroupOptions([]);
            setLoadingTrainingGroups(false);
            return;
        }

        // Get current bucket type
        const currentBucketType = form.getFieldValue("bucketType") || bucketType;

        // CRITICAL: Training groups only for GxP spaces
        if (currentBucketType?.toLowerCase() !== "gxp") {
            setGroupOptions([]);
            setLoadingTrainingGroups(false);
            return;
        }

        setLoadingTrainingGroups(true);
        try {
            const domainsData: any[] = JSON.parse(localStorage.getItem("domainsData") || "[]");
            const domain = domainsData.find(d => d.display_name === domainName);
            const trainingIds = domain?.training_ids || [];

            // If no training IDs, clear and return
            if (!hasTrainingIdsConfigured(domainName)) {
                setGroupOptions([]);
                return;
            }

            // Clear existing selected groups when switching to training-restricted domain
            // This prevents user confusion when previously selected groups don't appear in training-based dropdown
            // Only clear in create mode - in edit mode, groups come from editData and should be preserved also in editmode = true does not allow domain change
            if (mode === "create") {
                setGroups([{ group_name: "", access_level: "ro" }]);
                form.setFieldsValue({ groups: [{ group_name: "", access_level: "ro" }] });
            }

            // Fetch training data by IDs
            const trainingData = await fetchTrainingDataByIds(msalInstance, trainingIds);

            if (!trainingData || !trainingData.data || trainingData.data.length === 0) {
                setGroupOptions([]);
                return;
            }

            // Extract unique group NAMEs from training data
            const uniqueNames = [...new Set(
                trainingData.data
                    .map((record: { NAME: string }) => record.NAME)
                    .filter((name: string) => name && name.trim().length > 0),
            )];

            // If no valid names after filtering, return early
            if (uniqueNames.length === 0) {
                setGroupOptions([]);
                return;
            }

            // Fetch group details sequentially with delay to avoid rate limiting
            const allGroups: any[] = [];
            for (const name of uniqueNames) {
                try {
                    const groups = await fetchGroups(msalInstance, name);
                    allGroups.push(...groups);

                    // Add delay between API calls to avoid rate limiting (300ms)
                    if (uniqueNames.indexOf(name) < uniqueNames.length - 1) {
                        await new Promise(resolve => setTimeout(resolve, 300));
                    }
                } catch (error) {
                    console.error(`Failed to fetch group for name "${name}":`, error);
                    // Continue with next group even if one fails
                }
            }

            // Deduplicate groups by ID
            const uniqueGroups = allGroups.filter((group, index, self) =>
                index === self.findIndex(g => g.id === group.id),
            );

            // Filter out groups already selected
            const existingIds = groups
                .map(g => {
                    const m = g.group_name.match(/\(([^)]+)\)$/);
                    return m?.[1];
                })
                .filter(Boolean) as string[];

            const filteredGroups = uniqueGroups.filter(g => !existingIds.includes(String(g.id)));

            // Convert to options format
            const options = filteredGroups.map(group => ({
                label: group.name,
                value: String(group.id),
            }));

            // Set group options immediately so they appear in dropdown without typing
            setGroupOptions(options);
        } catch (error) {
            console.error("Error fetching training-based groups:", error);
            setGroupOptions([]);
        } finally {
            setLoadingTrainingGroups(false);
        }
    };

    /**
     * Handle primary domain change with GxP validation
     * @param value - The selected domain name
     */
    const handlePrimaryDomainChange = async (value: string) => {
        const currentBucketType = form.getFieldValue("bucketType") || bucketType;
        // Clear any requestor validation errors
        requestorErrorRef.current = false;
        form.setFields([
            {
                name: "requested_for",
                errors: [],
            },
        ]);

        // Fetch training-based groups for the selected domain (only for GxP)
        if (currentBucketType?.toLowerCase() === "gxp") {
            await fetchTrainingBasedGroups(value);
        } else {
            // Clear training groups for non-GxP
            setGroupOptions([]);
        }

        // Check if user has GxP selected and is changing to a non-validated domain
        if (currentBucketType?.toLowerCase() === "gxp" && !isDomainGxpValidated(value)) {
            // Show warning modal for non-validated domains
            setPendingBucketType("gxp");
            setGxpWarningTrigger("domain"); // Track this was triggered by domain change
            setShowGxpWarningModal(true);
        } else {
            // Directly set the domain
            setSelectedPrimaryDomain(value);

            // Check if switching to a domain with owner-group while GxP is selected
            const shouldEnforce = shouldEnforceGraphApi(currentBucketType, value);

            if (shouldEnforce) {
                await validateAndClearForAdminGroup(
                    value,
                    "This user is not a member of this domain's owner group: ",
                );
            }
        }
    };

    /**
     * Fetch parameters and secrets for edit mode
     */
    const fetchEditParametersSecrets = async (preserveLocalData = true) => {
        if (!editData?.name || !editData?.["primary-data-domain"]) {
            console.warn("Missing space name or domain for fetching parameters/secrets");
            setParameters([]);
            setSecrets([]);
            return;
        }

        setLoadingParametersSecrets(true);

        try {
            const data = await fetchParametersSecrets(
                msalInstance,
                editData.name,
                editData["primary-data-domain"],
            );

            if (data && (data.parameters.length > 0 || data.secrets.length > 0)) {
                // Decrypt the values for UI editing
                console.log("Fetched parameters/secrets data:", data);
                const decryptedParameters = data.parameters.map(param => {
                    const decryptedValue = decryptString(param.value);
                    console.log(`Fetched Decrypted parameter ${param.name}:`, decryptedValue);
                    return {
                        name: param.name, // Keep name as-is (no decryption needed)
                        value: param.value,
                        path: (param as any).path, // Include path if available
                        fromAPI: true, // Mark as from API
                    };
                });

                const decryptedSecrets = data.secrets.map(secret => {
                    const decryptedValue = decryptString(secret.value);
                    console.log(`Fetched Decrypted parameter ${secret.name}:`, decryptedValue);
                    return {
                        name: secret.name, // Keep name as-is (no decryption needed)
                        value: secret.value,
                        arn: (secret as any).arn, // Include arn if available
                        fromAPI: true, // Mark as from API
                    };
                });

                if (preserveLocalData) {
                    // Preserve locally added parameters (those without fromAPI flag)
                    const localParameters = parameters.filter(p => !(p as any).fromAPI && p.name.trim());
                    const localSecrets = secrets.filter(s => !(s as any).fromAPI && s.name.trim());

                    // Combine API data with local data
                    setParameters([...decryptedParameters, ...localParameters]);
                    setSecrets([...decryptedSecrets, ...localSecrets]);
                    
                    // Store original values for audit trail (only API data, not local)
                    setOriginalParameters(JSON.parse(JSON.stringify(decryptedParameters)));
                    setOriginalSecrets(JSON.parse(JSON.stringify(decryptedSecrets)));
                } else {
                    // Don't preserve local data (e.g., when canceling)
                    setParameters(decryptedParameters);
                    setSecrets(decryptedSecrets);
                    
                    // Store original values for audit trail
                    setOriginalParameters(JSON.parse(JSON.stringify(decryptedParameters)));
                    setOriginalSecrets(JSON.parse(JSON.stringify(decryptedSecrets)));
                }
            } else {
                if (preserveLocalData) {
                    // No API data found, keep only locally added items
                    const localParameters = parameters.filter(p => !(p as any).fromAPI && p.name.trim());
                    const localSecrets = secrets.filter(s => !(s as any).fromAPI && s.name.trim());

                    setParameters(localParameters.length > 0 ? localParameters : [{ name: "", value: "" }]);
                    setSecrets(localSecrets.length > 0 ? localSecrets : [{ name: "", value: "" }]);
                    
                    // Clear original values since no API data exists
                    setOriginalParameters([]);
                    setOriginalSecrets([]);
                } else {
                    // Reset to empty state
                    setParameters([{ name: "", value: "" }]);
                    setSecrets([{ name: "", value: "" }]);
                    
                    // Clear original values
                    setOriginalParameters([]);
                    setOriginalSecrets([]);
                }
            }
        } catch (error) {
            console.error("Failed to fetch parameters/secrets:", error);
            message.error("Failed to load parameters and secrets");
            if (preserveLocalData) {
                // On error, preserve locally added data
                const localParameters = parameters.filter(p => !(p as any).fromAPI && p.name.trim());
                const localSecrets = secrets.filter(s => !(s as any).fromAPI && s.name.trim());

                setParameters(localParameters.length > 0 ? localParameters : [{ name: "", value: "" }]);
                setSecrets(localSecrets.length > 0 ? localSecrets : [{ name: "", value: "" }]);
            } else {
                // Reset to empty state
                setParameters([{ name: "", value: "" }]);
                setSecrets([{ name: "", value: "" }]);
            }
        } finally {
            setLoadingParametersSecrets(false);
        }
    };

    /**
    * Processes an array of group data and extracts unique group IDs
    * based on access levels ("rw" for read-write, "ro" for read-only,
    * and "db_ro" for database read-only).
    * 
    * Group names are expected to contain an ID within parentheses, e.g., "Some Group Name (1234)".
    * 
    * @param {Group[]} [groupData=[]] - Array of group objects to process. Each object should contain a
    *   `group_name` string and an `access_level` string ("rw", "ro", or "db_ro").
    * 
    * @returns {IProcessedGroups} An object containing two arrays:
    *   - `rwGroupsArray`: Group IDs with read-write access.
    *   - `roGroupsArray`: Group IDs with read-only access.
    *   - `dbRoGroupsArray`: Group IDs with database read-only access.
    */
    const processGroups = (groupData: Group[] = []): IProcessedGroups => {
        const rwGroupsArray: string[] = [];
        const roGroupsArray: string[] = [];
        const regExp = /\(([^()]*)\)(?!.*\([^()]*)\)/; ///\(([^)]+)\)/;
        const dbRoGroupsArray: string[] = [];

        groupData.forEach(({ group_name, access_level }) => {

            const matches = [...group_name.matchAll(/\(([^)]+)\)/g)];
            if (matches && matches.length > 0) {
                const groupID = matches[matches.length - 1][1];
                if (access_level === "rw" && !rwGroupsArray.includes(groupID)) {
                    rwGroupsArray.push(groupID);
                } else if (access_level === "ro" && !roGroupsArray.includes(groupID)) {
                    roGroupsArray.push(groupID);
                } else if (access_level === "db_ro" && !dbRoGroupsArray.includes(groupID)) {
                    dbRoGroupsArray.push(groupID);
                }
            }
        });

        return { rwGroupsArray, roGroupsArray, dbRoGroupsArray };
    };

    /**
     * Get allowed data classifications for a domain
     * @param domainName - The domain display name
     * @returns Array of allowed classification values (defaults to ["SECRET", "RESTRICTED", "INTERNAL"])
     */
    function getDomainDataClassifications(domainName: string): string[] {
        const withDomainRule = (options: string[]) => classificationsForPrimaryDomain(domainName, options);

        if (!domainName) return withDomainRule(DEFAULT_CLASSIFICATIONS);

        try {
            const domainsData = JSON.parse(localStorage.getItem("domainsData")!) || [];
            const domain: any = domainsData.find((item: any) => item.display_name === domainName);

            if (!domain || !domain.data_classification || !Array.isArray(domain.data_classification)) {
                return withDomainRule(DEFAULT_CLASSIFICATIONS); // Fallback to all three
            }

            return withDomainRule(domain.data_classification as string[]);
        } catch (err) {
            console.error("Error getting domain data classifications:", err);
            return withDomainRule(DEFAULT_CLASSIFICATIONS);
        }
    }

    /**
     * Initialize parameters and secrets with appropriate defaults based on profile type
     * @param profileType - The selected profile type
     * @returns Object containing default parameters and secrets arrays
     */
    const initializeParametersAndSecrets = (profileType: string) => {
        if (profileType === "app_deployment_(BYOC)") {
            return {
                parameters: [{ name: "client-id", value: "" }],
                secrets: [{ name: "client-secret", value: "" }],
            };
        } else {
            return {
                parameters: [{ name: "", value: "" }],
                secrets: [{ name: "", value: "" }],
            };
        }
    };

    // Helper function to compare parameters/secrets and generate edited_params for audit trail
    const getEditedParams = () => {
        const edittedParams: Array<{ field: string; name: string }> = [];
        
        // Only compare if we're in edit mode and have original values
        if (mode !== "edit" || originalParameters.length === 0) {
            return edittedParams;
        }
        
        // Compare parameters
        const validOriginalParams = originalParameters.filter(p => p.name.trim() && p.value.trim());
        const validCurrentParams = parameters.filter(p => p.name.trim() && p.value.trim() && (p as any).fromAPI);
        
        // Check for modified/deleted parameters
        validOriginalParams.forEach(origParam => {
            const currentParam = validCurrentParams.find(p => p.name === origParam.name);
            if (!currentParam) {
                // Parameter was deleted - only send parameter name
                edittedParams.push({
                    field: "parameter_name",
                    name: origParam.name,
                });
            } else if (currentParam.value !== origParam.value) {
                // Parameter was modified - only send parameter name
                edittedParams.push({
                    field: "parameter_name",
                    name: origParam.name,
                });
            }
        });
        
        // Check for added parameters (new ones that aren't in original)
        validCurrentParams.forEach(currentParam => {
            const origParam = validOriginalParams.find(p => p.name === currentParam.name);
            if (!origParam) {
                // Parameter was added - only send parameter name
                edittedParams.push({
                    field: "parameter_name",
                    name: currentParam.name,
                });
            }
        });
        
        // Compare secrets
        const validOriginalSecrets = originalSecrets.filter(s => s.name.trim() && s.value.trim());
        const validCurrentSecrets = secrets.filter(s => s.name.trim() && s.value.trim() && (s as any).fromAPI);
        
        // Check for modified/deleted secrets
        validOriginalSecrets.forEach(origSecret => {
            const currentSecret = validCurrentSecrets.find(s => s.name === origSecret.name);
            if (!currentSecret) {
                // Secret was deleted - only send secret name
                edittedParams.push({
                    field: "secret_name",
                    name: origSecret.name,
                });
            } else if (currentSecret.value !== origSecret.value) {
                // Secret was modified - only send secret name
                edittedParams.push({
                    field: "secret_name",
                    name: origSecret.name,
                });
            }
        });
        
        // Check for added secrets
        validCurrentSecrets.forEach(currentSecret => {
            const origSecret = validOriginalSecrets.find(s => s.name === currentSecret.name);
            if (!origSecret) {
                // Secret was added - only send secret name
                edittedParams.push({
                    field: "secret_name",
                    name: currentSecret.name,
                });
            }
        });
        
        return edittedParams;
    };

    // Helper function to generate edited_params for create mode (all params/secrets are new)
    const getCreateModeEditedParams = () => {
        const edittedParams: Array<{ field: string; name: string }> = [];
        
        // Add all parameters as new additions
        const validParams = parameters.filter(p => p.name.trim() && p.value.trim());
        validParams.forEach(param => {
            edittedParams.push({
                field: "parameter_name",
                name: param.name,
            });
        });
        
        // Add all secrets as new additions
        const validSecrets = secrets.filter(s => s.name.trim() && s.value.trim());
        validSecrets.forEach(secret => {
            edittedParams.push({
                field: "secret_name",
                name: secret.name,
            });
        });
        
        return edittedParams;
    };


    // /**
    //  * Handle updating parameters and secrets using update-space API
    //  */
    // const handleUpdateParametersSecrets = async () => {
    //     if (!editData?.name || !editData?.["primary-data-domain"]) return;

    //     try {
    //         // Filter out empty parameters and secrets
    //         const validParams = parameters.filter(p => p.name.trim() && p.value.trim());
    //         const validSecrets = secrets.filter(s => s.name.trim() && s.value.trim());

    //         // Check if there are rows with partial data
    //         const partialParam = parameters.find(p => 
    //             (p.name.trim() && !p.value.trim()) || (!p.name.trim() && p.value.trim()),
    //         );
    //         const partialSecret = secrets.find(s => 
    //             (s.name.trim() && !s.value.trim()) || (!s.name.trim() && s.value.trim()),
    //         );

    //         if (partialParam || partialSecret) {
    //             message.error("Please fill both name and value for all entries, or leave both empty");
    //             return;
    //         }

    //         // Build the update payload using existing form values
    //         const formValues = form.getFieldsValue();
    //         const todayDate = new Date().toString();
    //         const ownersArray = (formValues?.owners?.map((user: { user_email: string }) => user.user_email) || []) as string[];

    //         const groupsResult = processGroups(formValues?.groups || []);
    //         const { rwGroupsArray, roGroupsArray } = groupsResult;
    //         const oldOwnersArray = editData?.mapped_users?.map(user => user.user_email) ?? [];
    //         const oldGroupsResult = processGroups(editData?.mapped_groups || []);
    //         const { rwGroupsArray: oldRwGroupsArray, roGroupsArray: oldRoGroupsArray } = oldGroupsResult;

    //         // Encrypt only the values within parameter/secret objects
    //         const encryptedParameters = validParams.map(param => ({
    //             name: param.name,
    //             value: encryptString(param.value),
    //         }));

    //         const encryptedSecrets = validSecrets.map(secret => ({
    //             name: secret.name,
    //             value: encryptString(secret.value),
    //         }));

    //         const updatePayload = {
    //             "bucket_name": editData?.name || "",
    //             "bucket_type": editData?.bucket_type,
    //             "space_name": editData?.name,
    //             "data_account_id": editData?.data_account_id,
    //             "ownersArray": ownersArray,
    //             "rwGroupsArray": rwGroupsArray,
    //             "roGroupsArray": roGroupsArray,
    //             "old_ownersArray": oldOwnersArray,
    //             "old_rwGroupsArray": oldRwGroupsArray,
    //             "old_roGroupsArray": oldRoGroupsArray,
    //             "sagemaker_domain": editData?.sagemaker_domain,
    //             "bucket_tech_name": editData?.bucket_tech_name || "",
    //             "metadata": {
    //                 ...editData,
    //                 "mapped_groups": JSON.stringify(formValues?.groups || []),
    //                 "mapped_users": JSON.stringify(formValues?.owners || []),
    //                 "last-modified-by": (() => {
    //                     try {
    //                         const accKeysJson = localStorage.getItem("msal.account.keys");
    //                         if (!accKeysJson) return "";
    //                         const accKeys: string[] = JSON.parse(accKeysJson);
    //                         const firstKey = accKeys[0];
    //                         if (!firstKey) return "";
    //                         const userDataJson = localStorage.getItem(firstKey);
    //                         if (!userDataJson) return "";
    //                         const userData: { username?: string } = JSON.parse(userDataJson);
    //                         return userData.username ?? "";
    //                     } catch (error) {
    //                         console.error("Failed to get logged-in user:", error);
    //                         return "";
    //                     }
    //                 })(),
    //                 "last-update": todayDate,
    //             },
    //             "mode": "Edit",
    //             // Add client_id and client_secret at root level
    //             "client_id": JSON.stringify(encryptedParameters),
    //             "client_secret": JSON.stringify(encryptedSecrets),
    //         };

    //         const success = await grantPrivileges(msalInstance, updatePayload, "edit");
    //         if (success) {
    //             message.success("Parameters and secrets updated successfully");
    //             setParametersModalVisible(false);
    //             if (onSuccess) onSuccess();
    //         } else {
    //             message.error("Failed to update parameters and secrets");
    //         }
    //     } catch (error) {
    //         console.error("Failed to update parameters/secrets:", error);
    //         message.error("Failed to update parameters and secrets");
    //     }
    // };

    const handleUpdateParametersSecretsLocalState = () => {
        // Filter out empty parameters and secrets
        const validParams = parameters.filter(p => p.name.trim() && p.value.trim());
        const validSecrets = secrets.filter(s => s.name.trim() && s.value.trim());
        
        // Check if there are rows with partial data
        const partialParam = parameters.find(p => 
            (p.name.trim() && !p.value.trim()) || (!p.name.trim() && p.value.trim()),
        );
        const partialSecret = secrets.find(s => 
            (s.name.trim() && !s.value.trim()) || (!s.name.trim() && s.value.trim()),
        );
        
        if (partialParam || partialSecret) {
            message.error("Please fill both name and value for all entries, or leave both empty");
            return;
        }
        
        // Require at least one parameter and one secret
        if (validParams.length === 0 || validSecrets.length === 0) {
            message.error("Please add at least one parameter and one secret");
            return;
        }
        
        // Update with only valid entries - save to local state
        setParameters(validParams);
        setSecrets(validSecrets);
        
        setParametersModalVisible(false);
        message.success("Parameters and secrets saved to local state. Click 'Save Changes' to apply all changes.");
    };


    const addOwner = () => setOwners([...owners, { user_email: "", access_level: "" }]);
    const removeOwner = (index: number) => setOwners(owners.filter((_, i) => i !== index));

    const addGroup = () =>
        setGroups([...groups, { group_name: "", access_level: "ro" }]);
    const removeGroup = (index: number) =>
        setGroups(groups.filter((_, i) => i !== index));

    /**
     * Resolve projectType and profileType from a gallery profile-type string.
     * Handles: direct project-type keys (data/analytics/dse_app),
     * specific profile-type values, and the "byoc" alias.
     */
    const resolveFromGalleryProfileType = (gpType: string): { projectType: string; profileType: string | null } => {
        const lower = gpType.toLowerCase();

        // Direct match to a project-type key
        if (profileOptionsMap[lower]) {
            const opts = profileOptionsMap[lower];
            return { projectType: lower, profileType: opts.length === 1 ? opts[0].value : null };
        }

        // Match against individual profile-type values
        for (const [projectType, opts] of Object.entries(profileOptionsMap)) {
            const match = opts.find(o => o.value.toLowerCase() === lower);
            if (match) return { projectType, profileType: match.value };
        }

        // "byoc" is an alias for dse_app / app_deployment_(BYOC)
        if (lower === "byoc") return { projectType: "dse_app", profileType: "app_deployment_(BYOC)" };

        return { projectType: "", profileType: null };
    };


    const handleRetentionTimeChange = (value: "period" | "date" | "experimentperiod") => {
        setRetentionTimeType(value);

        // Cleaning both fields, then the user only ever sees the relevant one
        if (value === "experimentperiod") {
            form.setFieldsValue({
                retentionTerm: retentionTermsExtended[0]?.value || "two_weeks", // Use first extended term as default
                retentionDate: undefined,
            });
        } else {
            form.setFieldsValue({
                retentionTerm: undefined,
                retentionDate: undefined,
            });
        }
    };

    const profileTypeDescriptions: any = {
        store_explore: "Deploys S3 (Raw), SUS Project (BYOR), asset crawler, metadata annotation, and Raw asset catalog publishing.",
        data_product_eng: "Deploys S3 (Raw, Bronze, Gold), SUS Project (Glue/EMR, workflows, Git), ELT compute, metadata annotation, and full asset catalog publishing.",
        store_analyze: "Store large datasets and run analytics workloads.",
        analytics_hpc: "Leverage high-performance computing for analytics tasks.",
        ml_feature_eng: "Develop and manage ML features and pipelines.",
        ml_model_eng: "Develop, train, and manage machine learning models.",
        "app_deployment_(BYOC)": "Deploy and manage your own applications (Bring Your Own Container).",
    };


    const getProfileTypeTooltip = (projectType: string) => {
        if (!projectType) return "Select 'Type of Project' to see options";
        const options = profileOptionsMap[projectType] || [];
        if (!options.length) return "No profile types available";
        return (
            <div>
                <div className="tooltip-content__title">Available Profile Types:</div>
                <ul className="tooltip-content__list">
                    {options.map(opt => (
                        <li key={opt.value} className="tooltip-content__list-item">
                            <strong>{opt.label}:</strong> {profileTypeDescriptions[opt.value]}
                        </li>
                    ))}
                </ul>
            </div>
        );
    };



    const getValidDomains = (data: Array<{ bucket_name: Record<string, string[]>; display_name: string, active: string }>): { display_name: string, disabled: boolean }[] => {
        return data
            .filter(item => Object.values(item.bucket_name).some(regionBuckets => regionBuckets.length >= 0))
            // .filter(item =>  item.active === "1") // filter active domains
            .map(item => ({ display_name: item.display_name, disabled: item.active === "0" })); //regionBuckets.length > 0 modify condition to filter
    };
    const getValidRegions = (
        data: Array<{ bucket_name: Record<string, string[]> }>,
    ): string[] => {
        const validRegions = data
            .flatMap(item => Object.entries(item.bucket_name))
            .filter(([_, buckets]) => buckets.length >= 0)
            .map(([region]) => region); //buckets.length > 0 modify condition to filter

        return [...new Set(validRegions)];
    };


    // Filter regions based on project type and profile type
    const getFilteredRegions = (allRegions: string[], projectType: string, profileType: string): string[] => {
        // Analytics ML Feature Eng and ML Model Eng are limited to us-east-1 and eu-central-1
        if (projectType === "analytics" && (profileType === "ml_feature_eng" || profileType === "ml_model_eng")) {
            const allowed = ["us-east-1", "eu-central-1"];
            return allRegions.filter(region => allowed.includes(region));
        }
        // Gen AI Eng is not available in ap-southeast-1
        if (profileType === "gen_ai_eng") {
            return allRegions.filter(region => region !== "ap-southeast-1");
        }
        return allRegions;
    };
    const getSecondaryDomains = (data: Array<{ bucket_name: Record<string, string[]>; display_name: string; active: string }>): string[] => {
        return data.filter(item => item.active === "1").map(item => item.display_name);
    };


    // Initialize with logged-in user email from storage
    const getLoggedInUser = (): string => {
        try {
            const accKeysJson = localStorage.getItem("msal.account.keys");
            if (!accKeysJson) return "";

            const accKeys: string[] = JSON.parse(accKeysJson);
            const firstKey = accKeys[0];
            if (!firstKey) return "";

            const userDataJson = localStorage.getItem(firstKey);
            if (!userDataJson) return "";

            const userData: MsalAccount = JSON.parse(userDataJson);
            return userData.username ?? "";
        } catch (error) {
            console.error("Failed to get logged-in user:", error);
            return "";
        }
    };

    useEffect(() => {
        if (mode !== "create" && editData?.mapped_groups?.length && oldGroupsRef.current === null) {
            oldGroupsRef.current = JSON.parse(JSON.stringify(editData.mapped_groups));
        }
    }, [mode, editData?.mapped_groups]);

    useEffect(() => {
        if (editData && mode !== "create") {
            const gitRepoNeededFromEdit = editData?.is_git_repo_needed === false
                || editData?.is_git_repo_needed === "false"
                ? false
                : true;
            setIsGitRepoNeeded(gitRepoNeededFromEdit);

            // Pre-populate the form fields if editing
            //setOldGroups(prev => prev.length ? prev : (editData?.mapped_groups || []));
            form.setFieldsValue({
                ...editData,
                spaceName: editData?.name,
                requested_for: editData?.requested_for || "",
                primaryDomain: editData?.["primary-data-domain"] || "",
                region: editData?.["region-name"],
                rdProject: editData?.rnd_project_id,
                clinicalStudyId: editData?.rnd_study_id,
                selection: !!editData?.rnd_study_id
                    ? safeParseArray(editData?.rnd_study_id)
                    : safeParseArray(editData?.rnd_project_id),
                licensingObligation: editData?.licensing_obligation,
                secondaryDomain: editData?.secondary_data_domain?.split(",").filter(domain => domain.trim()) || [],
                retentionType: editData?.retention_period_type === "period" &&
                    retentionTermsExtended.some(term => term.value === editData?.retention_period_value)
                    ? "experimentperiod"
                    : editData?.retention_period_type,
                retentionTerm: editData?.retention_period_type !== "date" ? editData?.retention_period_value : undefined,
                retentionDate: editData?.retention_period_type === "date" && editData?.retention_period_value
                    ? moment(editData.retention_period_value)
                    : undefined,
                dataSource: editData?.datasource,
                customGitLink: editData?.customGitLink,
                description: editData?.space_description,
                owners: editData?.mapped_users || [],
                groups: editData?.mapped_groups || [],
                projectType: editData?.project_type,
                bucketType: editData?.bucket_type,
                profileType: editData?.project_profile_type,
                registrationDate: decodeURIComponent(editData?.["registration-date"] ?? ""), //TODO update from api
                lastUpdatedBy: editData?.["last-modified-by"] || "",
                lastUpdatedOn: decodeURIComponent(editData?.["last-update"] ?? ""),
                needCloudGpuCompute: editData?.need_cloud_gpu_compute || "",
                need_rds_storage: editData?.need_rds_storage || "no",
                rds_storage_type: editData?.rds_storage_type || RDS_TYPE_DEFAULT,
                isGitRepoNeeded: gitRepoNeededFromEdit,
                fileSystem: gitRepoNeededFromEdit ? (editData?.file_system_type || "") : "",
                idCompliance: editData?.id_compliance || undefined,
            });
            setSelectedPrimaryDomain(editData?.["primary-data-domain"]);
            setBucketType(editData?.bucket_type || "");
			
            const loggedInUser = getLoggedInUser();
            setOwners(editData?.mapped_users || []);
            setGroups(editData?.mapped_groups || []);

            setProjectType(editData?.project_type || "");
            setProfileType(editData?.project_profile_type || "");

            // Pre-fill parameters and secrets from editData
            // Note: In edit mode, we'll fetch fresh data from the API when the modal opens
            // instead of relying on editData which may not have the latest BYOC parameters
            (() => {
                try {
                    // Check if editData has legacy client_id/client_secret format
                    if (editData?.client_id || editData?.client_secret) {
                        // Handle legacy format - parse from client_id and client_secret
                        const editParameters = editData?.client_id ?
                            JSON.parse(editData.client_id).map((param: { name: string; value: string; path?: string }) => {
                                const decryptedValue = decryptString(param.value);
                                return {
                                    name: param.name,
                                    value: decryptedValue,
                                    path: param.path,
                                };
                            }) : [];

                        const editSecrets = editData?.client_secret ?
                            JSON.parse(editData.client_secret).map((secret: { name: string; value: string; arn?: string }) => {
                                const decryptedValue = decryptString(secret.value);
                                return {
                                    name: secret.name,
                                    value: decryptedValue,
                                    arn: secret.arn,
                                };
                            }) : [];
                        setParameters(editParameters);
                        setSecrets(editSecrets);
                    } else {
                        // No legacy data found - will fetch from API when modal opens
                        setParameters([]);
                        setSecrets([]);
                    }
                } catch (error) {
                    console.error("Error parsing legacy parameters/secrets:", error);
                    setParameters([]);
                    setSecrets([]);
                }
            })();

            // External Data Exchange (CP) does not use project/study selection — skip hydration.
            if (!isExtDataExchange && editData?.rnd_project_id) {
                const isQaOrProd = ENVIRONMENT === "qa" || ENVIRONMENT === "prod";
                // rnd_project_id is stored as a JSON array of objects, e.g. '[{"project_id":"1",...}]'
                const parsedProjects = (safeParseArray(editData.rnd_project_id) || []) as { project_id?: string }[];
                const isSavedCommonProject = parsedProjects.length > 0 &&
                    parsedProjects.every(p => p.project_id === commonProject.project_id);

                if (isQaOrProd && isSavedCommonProject) {
                    // Common project is no longer available in QA/PROD — clear the selection
                    setSelectedProject(null);
                    setSelectedStudy(null);
                    setSelectedItems([]);
                    setSelectedType(null);
                    form.setFieldsValue({ rdProject: undefined, clinicalStudyId: undefined, selection: undefined });
                    message.warning("The previously selected Common Project has been removed as it is not available in this environment. Please select a required project/study and proceed to update the space.");
                } else {
                    setSelectedProject(editData?.rnd_project_id || null);
                    setSelectedItems(safeParseArray(editData?.rnd_project_id));
                    setSelectedType(!!editData?.rnd_project_id ? "project" : "study");
                }
            }
            if (!isExtDataExchange && editData?.rnd_study_id) {
                const isQaOrProd = ENVIRONMENT === "qa" || ENVIRONMENT === "prod";
                // rnd_study_id is stored as a JSON array of objects, e.g. '[{"study_id":"1",...}]'
                const parsedStudies = safeParseArray(editData.rnd_study_id) as { study_id?: string }[];
                const isSavedCommonStudy = parsedStudies.length > 0 &&
                    parsedStudies.every(s => s.study_id === commonStudy.study_id);

                if (isQaOrProd && isSavedCommonStudy) {
                    // Common study is no longer available in QA/PROD — clear the selection
                    setSelectedProject(null);
                    setSelectedStudy(null);
                    setSelectedItems([]);
                    setSelectedType(null);
                    form.setFieldsValue({ rdProject: undefined, clinicalStudyId: undefined, selection: undefined });
                    message.warning("The previously selected Common Study has been removed as it is not available in this environment. Please select a required project/study and proceed to update the space.");
                } else {
                    setSelectedStudy(editData?.rnd_study_id || null);
                    setSelectedItems(safeParseArray(editData?.rnd_study_id));
                    setSelectedType(!!editData?.rnd_study_id ? "study" : "project");
                }
            }

            const isNotSpaceOwner = !(editData?.mapped_users?.find(value => value.user_email == loggedInUser));
            setIsNotSpaceOwner(isNotSpaceOwner);

            // Set retention type state based on API data
            if (editData?.retention_period_type === "date") {
                setRetentionTimeType("date");
            } else if (editData?.retention_period_type === "period" &&
                retentionTermsExtended.some(term => term.value === editData?.retention_period_value)) {
                setRetentionTimeType("experimentperiod");
            } else if (editData?.retention_period_type === "period") {
                setRetentionTimeType("period");
            }

            // Automatically fetch parameters and secrets for edit mode
            fetchEditParametersSecrets(true);
        } else {
            // Reset to empty values for creating a new space
            // NOTE: form.resetFields() intentionally disabled to preserve current form input when switching to create mode.
            // form.resetFields();
            setSelectedProject(null);
            setSelectedStudy(null);
            setRetentionTimeType(RETENTION_DEFAULT_EXPERIMENT == "true" ? "experimentperiod" : "period"); // Reset to default based on env var
        }
    }, [editData, form]); // WIP: pre-fill the form in edit flow

    useEffect(() => {
        form.setFieldsValue({ owners });
    }, [owners, form]);

    useEffect(() => {
        form.setFieldsValue({ groups });
    }, [groups, form]);

    useEffect(() => {
        const domainsData = JSON.parse(localStorage.getItem("domainsData")!) || [];

        const validDomains = getValidDomains(domainsData);
        setDomainsList(validDomains);

        const domains = getSecondaryDomains(domainsData);
        setSecondaryDomain(domains);

        if (mode === "create") {
            //set the loggedIn user email for requeted_for field & primaryDomain and initial values
            const loggedInEmail = getLoggedInUser();
            const primaryDomainName = (domainName && validDomains.some(domain => domain.display_name === domainName))
                ? domainName
                : "";
            form.setFieldsValue({
                requested_for: loggedInEmail,
                primaryDomain: primaryDomainName,
                retentionType: RETENTION_DEFAULT_EXPERIMENT == "true" ? "experimentperiod" : "period",
                retentionTerm: RETENTION_DEFAULT_EXPERIMENT == "true"
                    ? retentionTermsExtended[0]?.value || "two_weeks"
                    : retentionTerms[0]?.value || "short",
                ...(!form.getFieldValue("need_rds_storage") ? { need_rds_storage: "no" } : {}),
                // projectType: "",
                // bucketType: "",
            });
            setSelectedPrimaryDomain(primaryDomainName);

            const updatedOwners = [...owners];
            updatedOwners[0] = {
                user_email: loggedInEmail,
                access_level: "owner",
            };
            setOwners(updatedOwners);

            // Initialize parameters and secrets with appropriate defaults
            if (parameters.length === 0) {
                const defaults = initializeParametersAndSecrets(profileType);
                setParameters(defaults.parameters);
            }
            if (secrets.length === 0) {
                const defaults = initializeParametersAndSecrets(profileType);
                setSecrets(defaults.secrets);
            }
        }
    }, [mode, form, domainName, profileType]);

    useEffect(() => {
        getGitList();
    }, []);



    useEffect(() => {
        const domainsData = JSON.parse(localStorage.getItem("domainsData")!) || [];
        const validRegions = getValidRegions(domainsData);
        const filteredRegions = getFilteredRegions(validRegions, projectType, profileType);
        setRegionsList(filteredRegions);


        if (mode !== "edit") {
            const currentRegion = form.getFieldValue("region");
            if (currentRegion && !filteredRegions.includes(currentRegion)) {
                form.setFieldsValue({ region: undefined });
            }
        }
    }, [projectType, profileType, form, mode]);

    // Handle parameters modal opening - ensure default empty rows in create mode
    useEffect(() => {
        if (parametersModalVisible && mode === "create") {
            if (parameters.length === 0) {
                const defaults = initializeParametersAndSecrets(profileType);
                setParameters(defaults.parameters);
            }
            if (secrets.length === 0) {
                const defaults = initializeParametersAndSecrets(profileType);
                setSecrets(defaults.secrets);
            }
        }
    }, [parametersModalVisible, mode, profileType, parameters.length, secrets.length]);

    // Fetch parameters and secrets when drawer opens in edit mode
    useEffect(() => {
        if (openMetadataForm && mode === "edit" && editData?.name && editData?.["primary-data-domain"]) {
            fetchEditParametersSecrets(true);
        }
    }, [openMetadataForm, mode]);


    const fetchRegionApi = async () => {
        const data = await fetchRegionData(msalInstance);

        if (data) {
            setProjects([commonProject, ...data.projects]);
            setClinicalStudy([commonStudy, ...data.studies]);
        }
    };

    useEffect(() => {
        fetchRegionApi();
    }, []);

    // Auto-select common project by default in create mode (skipped in qa/prod)
    useEffect(() => {
        if (mode === "create" && ENVIRONMENT !== "qa" && ENVIRONMENT !== "prod" && projects.length > 0 && !selectedProject && !selectedStudy) {
            if (commonProject) {
                const items = [commonProject];
                setSelectedType("project");
                setSelectedItems(items);
                setSelectedProject(JSON.stringify(items));
                setSelectedStudy(null);
                form.setFieldsValue({ selection: [commonProject.project_id] });
            }
        }
    }, [mode, projects, selectedProject, selectedStudy, form]);

    // Handle GxP bucket type selection and retention defaults
    useEffect(() => {
        if (mode === "create" && bucketType === "gxp") {
            // Auto-select Clinical study (25 years) for GxP in create mode
            setRetentionTimeType("period");
            form.setFieldsValue({
                retentionType: "period",
                retentionTerm: "gxp_long",
                retentionDate: undefined,
            });
        }
    }, [bucketType, mode, form]);

    // Update available classifications when primary domain changes
    useEffect(() => {
        if (selectedPrimaryDomain) {
            let allowedClassifications = getDomainDataClassifications(selectedPrimaryDomain);
            // Gallery-based creation must not offer SECRET
            if (mode === "create" && galleryProfileType) {
                allowedClassifications = allowedClassifications.filter(c => c !== "SECRET");
            }
            setAvailableClassifications(allowedClassifications);

            // Validate current classification value
            const currentClassification = form.getFieldValue("classification");
            if (currentClassification && !allowedClassifications.includes(currentClassification)) {
                // Clear the classification if it's not allowed for this domain
                form.setFieldsValue({ classification: undefined });
                message.warning(`Classification "${currentClassification}" is not allowed for domain "${selectedPrimaryDomain}". Please select a valid classification.`);
            }
        }
        // editData is a dependency so a re-hydration with the same domain re-validates the restored classification.
    }, [selectedPrimaryDomain, form, galleryProfileType, mode, editData]);

    // Apply gallery-based defaults when the drawer is opened for template-based creation
    useEffect(() => {
        if (mode !== "create" || !galleryProfileType) return;

        const resolved = resolveFromGalleryProfileType(galleryProfileType);

        const updates: Record<string, unknown> = {
            bucketType: "nongxp",
            isGitRepoNeeded: true,
        };

        if (resolved.projectType) {
            setProjectType(resolved.projectType);
            updates.projectType = resolved.projectType;
        }
        if (resolved.profileType) {
            setProfileType(resolved.profileType);
            profileTypeForValidatorRef.current = resolved.profileType;
            updates.profileType = resolved.profileType;
            // Initialise parameters/secrets for the resolved profile
            const defaults = initializeParametersAndSecrets(resolved.profileType);
            setParameters(defaults.parameters);
            setSecrets(defaults.secrets);
        }

        setBucketType("nongxp");
        setIsGitRepoNeeded(true);

        // Apply non-GxP retention defaults (mirrors handleBucketTypeChange's nongxp branch)
        const defaultType = RETENTION_DEFAULT_EXPERIMENT === "true" ? "experimentperiod" : "period";
        const defaultTerm = RETENTION_DEFAULT_EXPERIMENT === "true"
            ? retentionTermsExtended[0]?.value ?? "two_weeks"
            : "short";
        setRetentionTimeType(defaultType);
        updates.retentionType = defaultType;
        updates.retentionTerm = defaultTerm;

        form.setFieldsValue(updates);

        // Remove SECRET from available classifications for gallery-based creation
        setAvailableClassifications(prev => prev.filter(c => c !== "SECRET"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [galleryProfileType, mode]);

    // Fetch training-based groups when domain is auto-selected (on mount or edit)
    useEffect(() => {
        // Only for GxP spaces
        if (selectedPrimaryDomain && bucketType?.toLowerCase() === "gxp") {
            fetchTrainingBasedGroups(selectedPrimaryDomain);
        } else if (selectedPrimaryDomain && bucketType?.toLowerCase() === "nongxp") {
            // Clear training groups for non-GxP
            setGroupOptions([]);
        }
    }, [selectedPrimaryDomain, bucketType]);

    const ownersGroupsTotal = useMemo(() => {
        const ownersCount =
            (owners ?? []).filter(o => (o?.user_email ?? "").trim() !== "").length;
        const groupsCount =
            (groups ?? []).filter(g => (g?.group_name ?? "").trim() !== "").length;

        return ownersCount + groupsCount;
    }, [owners, groups]);

    const isOverLimit = ownersGroupsTotal > MAX_OWNERS_GROUPS;


    const handleClose = () => {
        // Reset the form and states when closing the drawer
        form.resetFields();
        setOwners([]);
        setGroups([]);
        setSelectedProject(null);
        setSelectedStudy(null);
        setParameters([]);
        setSecrets([]);
        onClose();
    };

    function getAwsAccountId(displayName: string): any | null {
        const domainsData = JSON.parse(localStorage.getItem("domainsData")!) || [];
        // Find the domain object with the matching display_name
        const domain: any = domainsData.find((item: any) => item.display_name === displayName);

        // If no matching domain is found, return null
        if (!domain) {
            return null;
        }

        // Return the aws_account_id if it exists, otherwise return null
        return domain.aws_account_id || null;
    }

    /**
     * Encrypt and attach client_id/client_secret to the payload if valid params and secrets exist.
     * Returns { client_id, client_secret } strings or undefined if nothing to attach.
     */
    const buildEncryptedCredentials = (): { client_id: string; client_secret: string } | null => {
        const validParams = parameters.filter(p => p.name.trim() && p.value.trim());
        const validSecrets = secrets.filter(s => s.name.trim() && s.value.trim());

        if (validParams.length > 0 && validSecrets.length > 0) {
            const encryptedParameters = validParams.map(param => ({
                name: param.name,
                value: encryptString(param.value),
            }));
            const encryptedSecrets = validSecrets.map(secret => ({
                name: secret.name,
                value: encryptString(secret.value),
            }));
            return {
                client_id: JSON.stringify(encryptedParameters),
                client_secret: JSON.stringify(encryptedSecrets),
            };
        }
        return null;
    };

    const handleSubmit = async (values: FormValues) => {
        const todayDate = new Date().toString(); // e.g., "Thu May 08 2025 11:28:43 GMT+0530 (India Standard Time)"
        const createdOn = todayDate;
        const secondary_data_domain = values?.secondaryDomain?.filter(domain => domain && domain.trim()).join(",");
        const loggedInUser = getLoggedInUser();

        let formPayload: IEditSpacePayload | ICreateSpacePayload;

        if (mode === "edit") {
            // Edit mode: send only the required fields
            const editPayload: IEditSpacePayload = {
                "space_name": values?.spaceName || null,
                "metadata": {
                    "name": values?.spaceName,
                    "mapped_groups": values?.groups[0]?.group_name === "" ? JSON.stringify([]) : JSON.stringify(values?.groups),
                    "classification": values?.classification,
                    "secondary_data_domain": secondary_data_domain || "",
                    "mapped_users": JSON.stringify(values?.owners),
                    "licensing_obligation": values?.licensingObligation,
                    "datasource": values?.dataSource ?? "",
                    "space_description": values?.description ?? "",
                    "retention_period_type": retentionTimeType === "period" || retentionTimeType === "experimentperiod" ? "period" : "date",
                    "retention_period_value": retentionTimeType === "period" || retentionTimeType === "experimentperiod" ? values?.retentionTerm ?? "" : values?.retentionDate?.format("YYYY-MM-DD") ?? "",
                    "need_cloud_gpu_compute": "",
                    "need_rds_storage": "",
                    "rds_storage_type": "",
                },
                "mode": "Edit",
            };

            const existingFileSystemType = (editData?.file_system_type ?? "").trim();
            const selectedFileSystem = isGxpSpaceType(values?.bucketType || editData?.bucket_type)
                ? ""
                : (values?.fileSystem ?? "").trim();
            if (existingFileSystemType === "" && selectedFileSystem !== "") {
                editPayload.file_system = selectedFileSystem;
            }

            // Conditionally add rnd_project_id or rnd_study_id based on selection type.
            // Not applicable for External Data Exchange (CP) — field is hidden and omitted from payload.
            if (!isExtDataExchange) {
                if (selectedType === "project" && selectedProject && selectedProject.length > 0) {
                    editPayload.metadata.rnd_project_id = selectedProject || "";
                } else if (selectedType === "study" && selectedStudy && selectedStudy.length > 0) {
                    editPayload.metadata.rnd_study_id = selectedStudy || "";
                }
            }

            // Add needCloudGpuCompute only if not analytics_hpc
            if (values.profileType !== "analytics_hpc" && values.needCloudGpuCompute) {
                editPayload.metadata.need_cloud_gpu_compute = values.needCloudGpuCompute;
            }
            // Add database provisioning fields for DSE App + BYOC
            if (values.projectType === "dse_app" && values.profileType === "app_deployment_(BYOC)") {
                editPayload.metadata.need_rds_storage = values.need_rds_storage || "";
                // Persist the chosen type only when storage is provisioned
                editPayload.metadata.rds_storage_type = values.need_rds_storage === "yes"
                    ? (values.rds_storage_type || RDS_TYPE_DEFAULT)
                    : "";
            }

            // Include encrypted parameters and secrets if configured
            const credentials = buildEncryptedCredentials();
            if (credentials) {
                editPayload.client_id = credentials.client_id;
                editPayload.client_secret = credentials.client_secret;
            }

            // Add audit trail for parameters/secrets
            const edittedParams = getEditedParams();
            if (edittedParams.length > 0) {
                editPayload.editted_params = edittedParams;
            }

            // ID Compliance only applies to the "External Data Exchange" domain.
            // Sent outside metadata; defaults to "" when not selected.
            if (isExtDataExchange) {
                editPayload.id_compliance = values?.idCompliance || "";
            }

            formPayload = editPayload;
        } else {
            // Create mode: send full payload
            const createPayload: ICreateSpacePayload = {
                "bucket_type": values?.bucketType,
                "space_name": values?.spaceName || null,
                "is_git_repo_needed": isGitRepoNeeded,
                "file_system": isGxpSpaceType(values?.bucketType) ? "" : (values?.fileSystem || ""),
                "metadata": {
                    "name": values?.spaceName,
                    "bucket_type": values?.bucketType,
                    "project_type": values?.projectType,
                    "project_profile_type": values?.profileType,
                    "mapped_groups": values?.groups[0]?.group_name === "" ? JSON.stringify([]) : JSON.stringify(values?.groups),
                    "classification": values?.classification,
                    "primary-data-domain": values?.primaryDomain,
                    "region-name": values?.region,
                    "secondary_data_domain": secondary_data_domain || "",
                    "mapped_users": JSON.stringify(values?.owners),
                    "licensing_obligation": values?.licensingObligation,
                    "datasource": values?.dataSource ?? "",
                    "customGitLink": (values?.customGitLink) ? "ph-rnd-dse-" + values?.customGitLink : "",
                    "template_git_url": parentTemplateRepo || "",
                    "space_description": values?.description ?? "",
                    "requested_for": values?.requested_for,
                    "retention_period_type": retentionTimeType === "period" || retentionTimeType === "experimentperiod" ? "period" : "date",
                    "retention_period_value": retentionTimeType === "period" || retentionTimeType === "experimentperiod" ? values?.retentionTerm ?? "" : values?.retentionDate?.format("YYYY-MM-DD") ?? "",
                    "need_cloud_gpu_compute": "",
                    "need_rds_storage": "",
                    "rds_storage_type": "",
                },
                // ID Compliance only applies to the "External Data Exchange" domain.
                // Sent outside metadata; defaults to "" when not selected.
                "id_compliance": isExtDataExchange ? (values?.idCompliance || "") : "",
                "mode": "Create",
            };

            // Not applicable for External Data Exchange (CP) — field is hidden and omitted from payload.
            if (!isExtDataExchange) {
                if (selectedType === "project" && selectedProject && selectedProject.length > 0) {
                    createPayload.metadata.rnd_project_id = selectedProject || "";
                } else if (selectedType === "study" && selectedStudy && selectedStudy.length > 0) {
                    createPayload.metadata.rnd_study_id = selectedStudy || "";
                }
            }

            // Include encrypted parameters and secrets if configured
            const credentials = buildEncryptedCredentials();
            if (credentials) {
                createPayload.client_id = credentials.client_id;
                createPayload.client_secret = credentials.client_secret;
            }

            const createParams = getCreateModeEditedParams();
            if (createParams.length > 0) {
                createPayload.create_params = createParams;
            }

            // Add needCloudGpuCompute only if not analytics_hpc
            if (values.profileType !== "analytics_hpc" && values.needCloudGpuCompute) {
                createPayload.metadata.need_cloud_gpu_compute = values.needCloudGpuCompute;
            }
            // Add database provisioning fields for DSE App + BYOC
            if (values.projectType === "dse_app" && values.profileType === "app_deployment_(BYOC)") {
                createPayload.metadata.need_rds_storage = values.need_rds_storage || "";
                // Persist the chosen type only when storage is provisioned
                createPayload.metadata.rds_storage_type = values.need_rds_storage === "yes"
                    ? (values.rds_storage_type || RDS_TYPE_DEFAULT)
                    : "";
            }

            formPayload = createPayload;
        }


        setIsSubmitting(true);
        try {
            const success = formPayload.mode === "Edit"
                ? await grantPrivileges(msalInstance, formPayload, "edit")
                : await grantPrivileges(msalInstance, formPayload, "create");
            
            if (success && "status" in success && success.status) {
                handleClose();
                message.success(success.message);
                if (onSuccess) onSuccess();
            } else {
                message.error({
                    content: "Space type (GxP or Non-GxP) cannot be changed after space creation.",
                    duration: 3,
                });
            }
        } catch (error: any) {
            if (error?.response?.status === 400 && error?.response?.data?.data?.errors) {
                const errors = error.response.data.data.errors;
                const errorListItems = Object.values(errors).map((err: any, index: number) => (
                    <li key={index}>{err.message as string}</li>
                ));

                message.error({
                    content: (
                        <div>
                            <div>Please correct the following errors:</div>
                            <ul>{errorListItems}</ul>
                        </div>
                    ),
                    duration: 5,
                });
                return;
            }

            message.error({
                content: error?.message || "Failed to save space. Please try again.",
                duration: 5,
            });
        } finally {
            if (onSuccess) onSuccess();
            setIsSubmitting(false);
        }
    };

    const getOwnersGroupsCount = (values: FormValues) => {
        const ownersCount = (values?.owners ?? []).filter(o => (o?.user_email ?? "").trim() !== "").length;
        const groupsCount = (values?.groups ?? []).filter(g => (g?.group_name ?? "").trim() !== "").length;

        return { ownersCount, groupsCount, total: ownersCount + groupsCount };
    };

    const handleFinish = (values: FormValues) => {

        const { ownersCount, groupsCount, total } = getOwnersGroupsCount(values);

        // Check if BYOC requires parameters and secrets
        if (values.profileType === "app_deployment_(BYOC)") {
            const validParams = parameters.filter(p => p.name.trim() && p.value.trim());
            const validSecrets = secrets.filter(s => s.name.trim() && s.value.trim());

            if (validParams.length === 0 || validSecrets.length === 0) {
                Modal.warning({
                    title: "Parameters and Secrets Required",
                    content: "For BYOC deployment, you must configure at least one parameter and one secret. Please click 'Configure Parameters and Secrets' to add them.",
                    okText: "OK",
                });
                return;
            }
        }

        // Block submit if Owners + Groups > 15
        if (total > MAX_OWNERS_GROUPS) {
            const needToRemove = total - (MAX_OWNERS_GROUPS);

            Modal.warning({
                title: "Limit Exceeded:",
                content: `You have selected ${ownersCount} owner(s) and ${groupsCount} group(s) (total ${total}). The maximum combined count is ${MAX_OWNERS_GROUPS}. Please remove at least ${needToRemove} to continue.`,
                okText: "OK",
            });
            return;
        }

        const confirmationProfileOption = (profileOptionsMap[values?.projectType || ""] || [])
            .find((opt: { value: string; label: string; estimated_time?: string }) => opt.value === values?.profileType);

        const fileSystemSelection = isGxpSpaceType(values?.bucketType) ? "" : (values?.fileSystem || "");
        const fileSystemExtraInfo = fileSystemSelection === "efs"
            ? { label: "EFS", extraMins: 5 }
            : fileSystemSelection === "fsx"
                ? { label: "FSx", extraMins: 15 }
                : undefined;

        const profileProvisioningEstimateForConfirmation = confirmationProfileOption?.estimated_time
            ? `Estimated space provisioning under ${confirmationProfileOption.label} is ${confirmationProfileOption.estimated_time} mins.${fileSystemExtraInfo ? ` Additional ${fileSystemExtraInfo.extraMins} mins for ${fileSystemExtraInfo.label} setup.` : ""}`
            : undefined;

        const creationSteps: Array<{ title: string; description: string }> = [
            ...(isGitRepoNeeded
                ? [{
                    title: "Git repo creation",
                    description: "A Git repository is provisioned for code and collaboration workflows when Git repo is required.",
                }]
                : []),
            {
                title: "SUS project creation and verification",
                description: "The SUS project is created and verified for readiness before downstream provisioning continues.",
            },
            {
                title: "S3 configuration (CORS)",
                description: "CORS policies are applied to the S3 bucket to support required browser-based integrations.",
            },
            {
                title: "S3 tagging",
                description: "Required metadata tags are applied to the bucket for governance, ownership, and cost tracking.",
            },
            {
                title: "Target role policy setup",
                description: "Target IAM role policies are attached/updated to enable secure access for the new space.",
            },
            {
                title: "Admin metadata form attachment",
                description: "Administrative metadata is attached to the space to support compliance and operational traceability.",
            },
            {
                title: "Access grants creation (S3 + IDC/SSO sync path)",
                description: "Access grants are provisioned for S3 and synchronized through the IDC/SSO entitlement path.",
            },
            ...(fileSystemSelection
                ? [{
                    title: `File system provision, verification, and attachment (${fileSystemSelection === "fsx" ? "FSx" : "EFS"})`,
                    description: `Selected file system (${fileSystemSelection === "fsx" ? "FSx" : "EFS"}) is provisioned, verified, and attached to the space.`,
                }]
                : []),
            {
                title: "Notifications and post-checks (MWAA asset, subscription checks, final notification)",
                description: "Post-provision checks are executed, then final notifications are sent after successful completion.",
            },
        ];

        const createConfirmationContent = (
            <div style={{
                            width: "100%",
                            }}>
                <div>
                    Space {values?.spaceName} is going to be created under {values?.region} of {values?.primaryDomain} domain.
                </div>
                {profileProvisioningEstimateForConfirmation && (
                    <div
                        style={{
                            width: "100%",
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 8,
                            color: "#1890ff",
                            marginTop: 12,
                            marginBottom: 12,
                        }}
                    >
                        <InfoCircleOutlined style={{ color: "#1890ff", marginTop: 4 }} />
                        <span>{profileProvisioningEstimateForConfirmation}</span>
                    </div>
                )}
                <div style={{ marginBottom: 8 }}>Space creation will go through these steps:</div>
                <Collapse accordion ghost style={{ marginBottom: 12 }}>
                    {creationSteps.map((step, index) => (
                        <Collapse.Panel header={step.title} key={`creation-step-${index}`}>
                            <p style={{ margin: 0 }}>{step.description}</p>
                        </Collapse.Panel>
                    ))}
                </Collapse>
                <div>Are you sure you want to proceed?</div>
            </div>
        );

        const confirmModalWidth = typeof window !== "undefined" &&
            typeof window.matchMedia === "function" &&
            window.matchMedia("(min-width: 768px)").matches
            ? 600
            : 416;

        Modal.confirm({
            className: "space-create-confirm-modal",
            width: confirmModalWidth,
            title: editData ? "Save Changes?" : "DSE Nexus Explorer: Confirm Space Creation",
            content: editData
                ? "Are you sure you want to save your edits?"
                : createConfirmationContent,
            okText: editData ? "Yes, Save" : "Yes, Submit",
            cancelText: "Cancel",
            onOk: () => {
                // only runs if user clicks “Yes…”
                handleSubmit(values);
            },
        });
    };

    /**
    * Debounced function to search for owners (users) based on input text.
    * 
    * Triggers a user search if the input length is at least 3 characters.
    * Uses `fetchUsers` to get matching users and updates the owner options
    * with formatted results. Debounce delay is 800ms to limit API calls.
    * 
    * @param {string} search - The search string entered by the user.
    * 
    * Dependencies:
    * - `msalInstance`: Authentication instance for making authorized API calls.
    * - `setFetchingOwners`: State setter to indicate loading status.
    * - `setOwnerOptions`: State setter to update the list of available owners.
    * 
    * Debounce:
    * - Implemented with `useDebouncedCallback` to reduce request frequency.
    */
    const debouncedSearch = useDebouncedCallback(
        async (search: string) => {
            // Check if current domain has training IDs configured
            const currentDomain = selectedPrimaryDomain || form.getFieldValue("primaryDomain");
            const currentBucketType = form.getFieldValue("bucketType") || bucketType;

            // If GxP space with training IDs, always show only training-based groups (search is disabled)
            if (currentBucketType?.toLowerCase() === "gxp" && hasTrainingIdsConfigured(currentDomain)) {
                // Training groups are already set in groupOptions by fetchTrainingBasedGroups
                // No need to do anything here - just ignore the search
                return;
            }

            // Normal search flow (when no training IDs configured)
            if (search.length >= 3) {
                setFetchingGroups(true);
                const fetched = await fetchGroups(msalInstance, search);

                // 1) pull out IDs of already-selected groups
                const existingIds = groups
                    .map(g => {
                        const m = g.group_name.match(/\(([^)]+)\)$/);
                        return m?.[1];
                    })
                    .filter(Boolean) as string[];

                const opts = fetched
                    .filter(g => !existingIds.includes(String(g.id)))
                    .map(group => ({
                        label: group.name,
                        value: String(group.id),
                    }));

                setGroupOptions(opts);
                setFetchingGroups(false);
            } else {
                setGroupOptions([]);
            }
        },
        800,
    );




    const debouncedOwnerSearch = useDebouncedCallback(
        async (search: string, type: string) => {
            if (search.length >= 3) {
                setFetchingOwners(true);

                // Check if GxP space AND domain has owner-group
                const currentBucketType = form.getFieldValue("bucketType") || bucketType;
                const currentDomain = form.getFieldValue("primaryDomain") || selectedPrimaryDomain;
                const isGxp = currentBucketType === "gxp";

                let shouldUseGraphApi = false;
                let groupIds: string[] = [];

                if (isGxp && currentDomain) {
                    try {
                        const domainsData: any[] = JSON.parse(localStorage.getItem("domainsData") || "[]");
                        const domain = domainsData.find(d => d.display_name === currentDomain);
                        const ownerGroups = domain?.owner_group || [];
                        groupIds = ownerGroups
                            .map((g: any) => g?.id as unknown)
                            .filter((id: unknown): id is string => typeof id === "string" && id.length > 0);
                        shouldUseGraphApi = groupIds.length > 0;
                    } catch (err) {
                        console.error("Error checking owner-group:", err);
                    }
                }

                if (shouldUseGraphApi && groupIds.length > 0) {
                    // Fetch from Microsoft Graph API (owner group members from all groups)
                    try {
                        const members = await fetchGroupMembers(msalInstance, groupIds, search);

                        //array of existing owner emails for lookup
                        const existingOwnerEmails = owners.map(owner => (owner.user_email ?? "").toLowerCase());

                        // Filter out users who are already in the owners list
                        const filteredMembers = type === "owner"
                            ? members.filter(member =>
                                member.mail && !existingOwnerEmails.includes(member.mail.toLowerCase()),
                            )
                            : members.filter(member => member.mail); // Ensure mail exists

                        setOwnerOptions(
                            filteredMembers.map((member) => ({
                                label: member.mail || member.displayName || member.id,
                                email: member.mail || "",
                                value: member.id,
                            })),
                        );
                    } catch (error) {
                        console.error("Error fetching group members:", error);
                        setOwnerOptions([]);
                    }
                } else {
                    // Use existing backend API
                    const users = await fetchUsers(msalInstance, search);

                    //array of existing owner emails for lookup
                    const existingOwnerEmails = owners.map(owner => (owner.user_email ?? "").toLowerCase());

                    // Filter out users who are already in the owners list
                    const filteredUsers = type === "owner" ? users.filter(user =>
                        user.mail && !existingOwnerEmails.includes(user.mail.toLowerCase()),
                    ) : users;

                    setOwnerOptions(
                        filteredUsers.map((user) => ({
                            label: user.mail,
                            email: user.mail,
                            value: user.id,
                        })),
                    );
                }

                setFetchingOwners(false);
            } else {
                setOwnerOptions([]);
            }
        },
        800,
    );

    const checkSpaceNameExists = async (value: string) => {
        const data = await validateSpaceName(msalInstance, value);
        return data;
    };

    /**
    * Handles blur event for the Clinical Study ID field.
    * Validates the ID by fetching related project data and updates the form accordingly.
    */
    const handleStudyBlur = async () => {
        const selectedStudy = form.getFieldValue("clinicalStudyId");
        if (!selectedStudy) return;

        const result = await getProjectsAndStudies(msalInstance, selectedStudy);

        if (result.matched) {
            form.setFieldsValue({
                rdProject: result.projectDisplayName,
            });
        } else {
            form.setFields([
                {
                    name: "clinicalStudyId",
                    errors: ["Input provided for Clinical Study ID is invalid"],
                },
            ]);
        }
    };

    const handleSelection = (type: "project" | "study", selected: string[]) => {
        // Update the form's selection field with the selected IDs
        form.setFieldsValue({ selection: selected });
        setModalOpen(false);

        // Map selected IDs to their corresponding items for display
        const items = type === "project"
            ? projects.filter((project) => selected.includes(project.project_id))
            : clinicalStudy.filter((study) => selected.includes(study.study_id));

        setSelectedType(type);
        setSelectedItems(items);

        // Update state variables based on selection type
        if (type === "project" && items.length > 0) {
            setSelectedProject(JSON.stringify(items));
            setSelectedStudy(null);
        } else if (type === "study" && items.length > 0) {
            setSelectedStudy(JSON.stringify(items));

        } else {
            setSelectedProject(null);
            setSelectedStudy(null);
            form.setFieldsValue({
                rdProject: undefined,
                clinicalStudyId: undefined,
            });
        }

    };

    const handleDefaultSelection = useCallback(() => {
        // External Data Exchange (CP) does not use project/study selection.
        if (isExtDataExchange) return;
        if (!editData && projects.length > 0) {
            if (commonProject && ENVIRONMENT !== "qa" && ENVIRONMENT !== "prod") {
                handleSelection("project", [commonProject.project_id]);
            }
        }
    }, [projects, editData, isExtDataExchange]);

    useEffect(() => {
        handleDefaultSelection();
    }, [handleDefaultSelection]);

    const selectedColumns: ColumnsType<ItemType> =
        selectedType === "project"
            ? [
                {
                    title: "Project Name (ID)",
                    dataIndex: "project_name",
                    key: "project_name",
                    render: (_: any, record: ItemType) =>
                        "project_id" in record
                            ? `${record.project_name} (${record.project_id})`
                            : "",
                },
            ]
            : [
                {
                    title: "Study Name (ID)",
                    dataIndex: "study_name",
                    key: "study_name",
                    render: (_: any, record: ItemType) =>
                        "study_id" in record
                            ? `${record.study_name} (${record.study_id})`
                            : "",
                },
                {
                    title: "Project Name (ID)",
                    dataIndex: "project_name",
                    key: "project_name",
                    render: (_: any, record: ItemType) =>
                        "project_id" in record
                            ? `${record.project_name} (${record.project_id})`
                            : "",
                },
            ];

    const projectTypeOptions = useMemo(() => {
        const options = profileOptionsMap[projectType] || [];
        const userGroupsRaw = msalInstance?.accounts[0]?.idTokenClaims?.groups;
        const userGroups: string[] = Array.isArray(userGroupsRaw) ? userGroupsRaw : [];
        const isAccessToHPC = GROUP_FOR_HPC_PROJECT_CLAIM;

        // Allow "analytics_hpc" ONLY if a group claim exists AND the user is in that group
        const canAccessHPC =
            isAccessToHPC !== undefined && userGroups.includes(isAccessToHPC);

        if (canAccessHPC) {
            return options;
        }

        // Otherwise remove it
        return options.filter(option => option.value !== "analytics_hpc");
    }, [projectType, msalInstance?.accounts]);

    const showCustomFileSystem = !isGxpSpaceType(
        mode === "edit" ? (editData?.bucket_type || bucketType) : bucketType,
    );

    return (
        <Drawer
            title={editData ? "Edit Space" : "Create New Space"}
            width="80%"
            onClose={handleClose}
            open={openMetadataForm}
            //footer prop with the buttons
            footer={
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <Space>
                        <Button
                            onClick={handleClose}
                            disabled={isSubmitting}
                        >
                            {"Cancel"}
                        </Button>
                        <Button
                            onClick={() => form.submit()}
                            type={"primary"}
                            disabled={isNotSpaceOwner}
                            loading={isSubmitting}
                        >
                            {editData ? "Save Changes" : "Submit"}
                        </Button>
                    </Space>
                </div>
            }
        >
            {(loading || isSubmitting) ? (
                isSubmitting ? (
                    <div
                        className="space__spinner-overlay"
                    >
                        <Spin tip={isSubmitting ? "Submiting..." : "Loading..."} size="large" ><div style={contentStyle}></div></Spin>
                    </div>) :
                    <FormSkeleton
                        layout={skeletonData}
                    />

            ) : (
                <Form layout="vertical" onFinish={handleFinish} hideRequiredMark={false} form={form}>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="spaceName"
                                label={
                                    <span>
                                        Space Name&nbsp;
                                        <Tooltip
                                            title={
                                                <div>
                                                    <strong>Space Name Requirements:</strong>
                                                    <ol style={{ marginTop: 6 }}>
                                                        <li>Minimum length: 5 characters</li>
                                                        <li>Maximum length: {profileType === "ml_model_eng" ? 30 : 100} characters</li>
                                                        <li>Must contain at least one letter or one number</li>
                                                        <li>Underscore (_) not allowed</li>
                                                        <li>Hash (#) not allowed</li>
                                                        <li>Special characters are not allowed (only letters, numbers, and hyphens)</li>
                                                        <li>Cannot start or end with hyphens (-)</li>
                                                        <li>Cannot contain consecutive hyphens (--)</li>
                                                        <li>Spaces will be automatically replaced with hyphens (-)</li>
                                                        <li>Long names will be automatically trimmed and made unique</li>
                                                        <li>Must be unique (for new spaces)</li>
                                                    </ol>
                                                </div>
                                            }
                                            overlayInnerStyle={{ minWidth: 320, maxWidth: 450 }}
                                        >
                                            <InfoCircleOutlined style={{ color: "#1890ff" }} />
                                        </Tooltip>
                                    </span>
                                }
                                validateTrigger="onBlur"
                            rules={[
                                { required: true, message: "Please enter space name." },
                                { min: 5, message: "Space name must be at least 5 characters long." },

                                {
                                    validator: async (_, value) => {
                                        if (!value) return Promise.resolve();

                                        // For ML Model Eng, appImageConfigName = "{space_name}-sagemaker-image-v1-jupyterlab"
                                        // suffix "-sagemaker-image-v1-jupyterlab" = 30 chars; 63 (AWS max) - 30 = 33
                                        if (profileTypeForValidatorRef.current === "ml_model_eng" && value.length > 30) {
                                            return Promise.reject(
                                                new Error("Space name must be at most 30 characters for the ML Model Eng profile type."),
                                            );
                                        }

                                        if (isOverLimit) return Promise.resolve();

                                        if (value.length > 100) {
                                            return Promise.reject(
                                                new Error("Maximum character limit reached for Space name."),
                                            );
                                        }

                                        // Must contain at least one letter or one number
                                        const hasLetter = /[a-zA-Z]/.test(value);
                                        const hasNumber = /[0-9]/.test(value);
                                        if (!hasLetter && !hasNumber) {
                                            return Promise.reject(
                                                new Error("Space name must contain at least one letter or one number."),
                                            );
                                        }

                                        // Explicit disallowed characters
                                        if (value.includes("_")) {
                                            return Promise.reject(new Error("Underscore is not allowed under Space name field."));
                                        }

                                        if (value.includes("#")) {
                                            return Promise.reject(new Error("'#' is not allowed under Space name field."));
                                        }

                                        // NEW: reject any special characters except hyphens
                                        if (!/^[A-Za-z0-9- ]+$/.test(value)) {
                                            return Promise.reject(
                                                new Error("Special characters are not allowed. Only letters, numbers, and hyphens are valid."),
                                            );
                                        }

                                        // Cannot start or end with hyphen
                                        if (value.startsWith("-") || value.endsWith("-")) {
                                            return Promise.reject(
                                                new Error("Space name cannot start or end with hyphens."),
                                            );
                                        }

                                        // No consecutive hyphens
                                        if (/--+/.test(value)) {
                                            return Promise.reject(
                                                new Error("Space name cannot contain consecutive hyphens."),
                                            );
                                        }

                                        // Check unique if create mode
                                        if (mode === "create") {
                                            const exists = await checkSpaceNameExists(value);
                                            if (exists) {
                                                return Promise.reject(
                                                    new Error(`Space name (i.e. ${value}) already exists, please choose a unique name.`),
                                                );
                                            }
                                        }

                                        return Promise.resolve();
                                    },
                                },
                            ]}
                            >
                                <>
                                    <Input
                                        id="spaceName"
                                        placeholder="Please enter space name"
                                        value={form.getFieldValue("spaceName") || ""}
                                        onChange={(e) => {
                                            const inputValue = e.target.value;

                                            // replace spaces with hyphens
                                            const formatted = inputValue.replaceAll(" ", "-");

                                            form.setFieldsValue({ spaceName: formatted });
                                            setSpaceNameCount(formatted.length);

                                            // Live feedback for the ml_model_eng 30-char limit
                                            if (profileTypeForValidatorRef.current === "ml_model_eng") {
                                                if (formatted.length > 30) {
                                                    form.setFields([{ name: "spaceName", errors: ["Space name must be at most 30 characters for the ML Model Eng profile type."] }]);
                                                } else {
                                                    form.setFields([{ name: "spaceName", errors: [] }]);
                                                }
                                            }
                                        }}
                                        disabled={mode === "edit" ? true : false || isSubmitting}
                                    />

                                    <div style={{ display: "flex", justifyContent: spaceNameCount > 63 && profileType !== "ml_model_eng" ? "space-between" : "flex-end", marginTop: 4, textAlign: "right" }}>
                                        {spaceNameCount > 63 && profileType !== "ml_model_eng" && (
                                            <span style={{ display: "block", color: "#faad14", fontSize: 12, textAlign: "start" }}>
                                                {spaceNameCount >= 100
                                                    ? "Maximum character limit reached. Space name will be trimmed for GitHub, SUS Project, and S3 bucket creation."
                                                    : "Space name exceeds 63 characters and will be trimmed for GitHub, SUS Project, and S3 bucket creation."
                                                }
                                            </span>
                                        )}
                                        <p style={{ color: "#00000073", whiteSpace: "nowrap" }}>
                                            {spaceNameCount} / {profileType === "ml_model_eng" ? 30 : 100}
                                        </p>
                                    </div>
                                </>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="requested_for"
                                label="Requested for"
                                rules={[
                                    { required: true, message: "Please provide valid Bayer email of the requestor of this Space." },
                                    {
                                        validator: () => {
                                            if (!requestorErrorRef.current) return Promise.resolve();
                                            const msg = (
                                                <span>
                                                    This user is not a member of the domain's owner group: <strong>{requestorErrorMessageRef.current}</strong>.{" "}
                                                    <a href={`${USER_GUIDE_URL}#owner-group-validation-for-restricted-domains`} target="_blank" rel="noopener noreferrer" className="help-link">
                                                        <InfoCircleOutlined style={{ marginRight: 4 }} />
                                                        Learn more
                                                    </a>
                                                </span>
                                            );
                                            return Promise.reject(msg);
                                        },
                                    },
                                ]}
                            >
                                <Select
                                    showSearch
                                    allowClear
                                    placeholder="Please enter email"
                                    filterOption={false}
                                    onSearch={(value) => debouncedOwnerSearch(value, "requestor")}
                                    style={{ width: "100%" }}
                                    onChange={(value) => {
                                        const currentDomain = form.getFieldValue("primaryDomain") || selectedPrimaryDomain;
                                        const currentBucketTypeVal = form.getFieldValue("bucketType") || bucketType;

                                        if (shouldEnforceGraphApi(currentBucketTypeVal, currentDomain)) {
                                            // Clear existing error immediately so UI feels responsive
                                            requestorErrorRef.current = false;
                                            form.setFields([{ name: "requested_for", errors: [] }]);

                                            // Re-validate the newly selected user against owner group
                                            if (value) {
                                                validateAndClearForAdminGroup(
                                                    currentDomain,
                                                    "This user is not a member of the domain's owner group: ",
                                                    false,
                                                );
                                            }
                                        }
                                    }}
                                    onBlur={(e) => {
                                        const email = form.getFieldValue("requested_for");
                                        // Only auto-populate owners[0] when NOT a GxP space with owner-group
                                        // enforcement. For GxP + owner-group domains the requestor validation
                                        // is handled by validateAndClearForAdminGroup; blindly overwriting
                                        // owners[0] here would bypass that check and cause unexpected state.
                                        if (!shouldEnforceGraphApi(form.getFieldValue("bucketType") || bucketType, form.getFieldValue("primaryDomain") || selectedPrimaryDomain)) {
                                            const updatedOwners = [...owners];
                                            updatedOwners[0] = {
                                                user_email: email,
                                                access_level: "owner",
                                            };
                                            setOwners(updatedOwners);
                                        }
                                        setOwnerOptions([]); // Clear options after selection
                                    }}
                                    disabled={mode === "edit" ? true : false || isSubmitting}
                                >
                                    {ownerOptions.map((opt) => (
                                        <Option key={opt.value} value={opt.label}>
                                            {opt.label}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>


                    {/* Two columns for Project Type and Profile */}
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="Type of Project"
                                name="projectType"
                                rules={[{ required: true, message: "Select project type" }]}
                            >
                                <Select
                                    placeholder="Select project type"
                                    onChange={value => {
                                        setProjectType(value);
                                        setProfileType("");
                                        form.setFieldsValue({ profileType: "" });
                                        // Clear validation errors for applicationParamsSecrets field when project type changes
                                        form.setFields([{
                                            name: "applicationParamsSecrets",
                                            errors: [],
                                        }]);
                                    }}
                                    style={{ width: "100%" }}
                                    disabled={mode === "edit" ? true : isNotSpaceOwner || isSubmitting}
                                >
                                    <Option value="data">Data</Option>
                                    {(!isExtDataExchange || projectType === "analytics") && (
                                        <Option value="analytics" disabled={isExtDataExchange}>Analytics</Option>
                                    )}
                                    <Option value="dse_app">DSE App</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label={
                                    <span>
                                        Project Profile Type&nbsp;
                                        <Tooltip title={getProfileTypeTooltip(projectType)} overlayInnerStyle={{ minWidth: 340, maxWidth: 500 }}>
                                            <InfoCircleOutlined style={{ color: "#1890ff" }} />
                                        </Tooltip>
                                    </span>
                                }
                                name="profileType"
                                rules={[{ required: true, message: "Select profile Type" }]}
                            >
                                <Select
                                    placeholder="Select profile type"
                                    value={profileType}
                                    onChange={value => {
                                        setProfileType(value);
                                        profileTypeForValidatorRef.current = value; // sync immediately before setTimeout fires
                                        form.setFieldsValue({ needCloudGpuCompute: undefined, need_rds_storage: "", rds_storage_type: undefined });
                                        // Clear any stale spaceName error then re-validate with the new profile type
                                        form.setFields([{ name: "spaceName", errors: [] }]);
                                        setTimeout(() => form.validateFields(["spaceName"]).catch(() => {}), 0);

                                        // Set isGitRepoNeeded to false if profile is not store_explore or store_analyze
                                        if (value === "store_explore" || value === "store_analyze") {
                                            setIsGitRepoNeeded(false);
                                        } else {
                                            setIsGitRepoNeeded(true);
                                        }

                                        // Reset parameters and secrets with appropriate defaults when profile type changes
                                        const defaults = initializeParametersAndSecrets(value);
                                        setParameters(defaults.parameters);
                                        setSecrets(defaults.secrets);
                                    }}
                                    style={{ width: "100%" }}
                                    disabled={mode === "edit" ? true : isNotSpaceOwner || isSubmitting}
                                >
                                    {(projectTypeOptions || []).map(opt => (
                                        <Option key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    {/* Git Repo Needed + Use Existing GitHub Repo */}
                    {mode === "create" && (
                        <Row gutter={16}>
                            {(profileType === "store_explore" || profileType === "store_analyze") && (
                                <Col span={12}>
                                    <Form.Item
                                        name="isGitRepoNeeded"
                                        label={
                                            <span>
                                                Git Repo Needed
                                                <Tooltip title="Select 'Yes' if you need a Git repository for SUS project storage. If 'No' is selected, the 'Use Existing Github Repo' field will be disabled.">
                                                    <InfoCircleOutlined style={{ color: "#1890ff", marginLeft: 8 }} />
                                                </Tooltip>
                                            </span>
                                        }
                                        style={{ marginBottom: 16 }}
                                    >
                                        <Radio.Group
                                            value={isGitRepoNeeded}
                                            onChange={(e) => {
                                                setIsGitRepoNeeded(e.target.value);
                                                // Reset isGitLink to false when Git Repo is not needed
                                                if (e.target.value === false) {
                                                    setisGitLink(false);
                                                    form.setFieldValue("customGitLink", "");
                                                    form.setFieldValue("fileSystem", undefined);
                                                }
                                            }}
                                            defaultValue={false}
                                            disabled={isNotSpaceOwner || isSubmitting}
                                        >
                                            <Radio value={true}>Yes</Radio>
                                            <Radio value={false}>No</Radio>
                                        </Radio.Group>
                                    </Form.Item>
                                </Col>
                            )}
                            <Col span={12}>
                                <Form.Item
                                    label={
                                        <span>
                                            Use Existing GitHub Repo&nbsp;
                                            <Tooltip
                                                title={
                                                    <span>
                                                        This repository is tagged with <code>beat-not-applicable</code> by default. If applicable, replace it with your BEAT ID. {" "}
                                                        <a
                                                            href="https://docs.int.bayer.com/cloud/devops/github/conventions/tagging/"
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="help-link"
                                                        >
                                                            Learn more
                                                        </a>
                                                    </span>
                                                }
                                            >
                                                <InfoCircleOutlined style={{ color: "#1890ff" }} />
                                            </Tooltip>
                                        </span>
                                    }
                                >
                                    <div className="custom-git-link">
                                        <Radio.Group
                                            onChange={(e) => handleExistingLink(e.target.value)}
                                            value={isGitRepoNeeded ? (isGitLink ? "" : "No") : "No"}
                                            disabled={!isGitRepoNeeded}
                                            className="custom-git-link-radio"
                                        >
                                            <Radio value="">Yes</Radio>
                                            <Radio value="No">No</Radio>
                                        </Radio.Group>
                                        {isGitLink && (
                                            <div className="custom-git-link-details">
                                                <Form.Item>
                                                    <Tooltip
                                                        title={"Only Repositories under Nexus self service portal will be authorized to create a project."}
                                                        overlayClassName="custom-git-link-tooltip-overlay"
                                                    >
                                                        <span className="custom-git-link-tooltip">
                                                            <InfoCircleOutlined style={{ color: "#1890ff" }} />
                                                            {" "}
                                                            ph-rnd-dse-
                                                        </span>
                                                    </Tooltip>
                                                </Form.Item>

                                                <Form.Item
                                                    name="customGitLink"
                                                    rules={[
                                                        { required: true, message: "Please select a repo." },
                                                    ]}
                                                >
                                                    <Select
                                                        placeholder={GitList.length === 0 ? "Loading..." : "Select GitHub Repo Name"}
                                                        showSearch
                                                        onChange={(e) => {
                                                            form.setFieldsValue({ customGitLink: e });
                                                        }}
                                                        disabled={isSubmitting || GitList.length === 0}
                                                        dropdownMatchSelectWidth={400}
                                                        className="custom-git-link-select"
                                                    >
                                                        {GitList.map(GitRepo => (
                                                            <Option key={GitRepo} value={GitRepo}>
                                                                {GitRepo}
                                                            </Option>
                                                        ))}
                                                    </Select>
                                                </Form.Item>
                                            </div>
                                        )}
                                    </div>
                                </Form.Item>
                            </Col>
                        </Row>
                    )}

                    {/* Custom Git Link (edit mode) */}
                    {mode !== "create" && (
                        <Row gutter={16}>
                            <Col span={24}>
                                <Form.Item
                                    name="customGitLink"
                                    label="GitHub Repo Name:"
                                >
                                    <Input inputMode="text" disabled={true} />
                                </Form.Item>
                            </Col>
                        </Row>
                    )}

                    <Row gutter={16} style={{ marginBottom: 24 }}>
                        <Col span={24}>
                            <Form.Item
                                label={
                                    <span>
                                        Application Parameters & Secrets
                                        <Tooltip title={profileType === "app_deployment_(BYOC)" ? "Configure application parameters and secrets for BYOC deployment (Required)" : "Configure application parameters and secrets for deployment (Optional)"}>
                                            <InfoCircleOutlined style={{ color: "#1890ff", marginLeft: 4 }} />
                                        </Tooltip>
                                        {profileType === "app_deployment_(BYOC)" && (
                                            <a
                                                href={BYOC_SETUP_GUIDE_URL}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="help-link"
                                                style={{ marginLeft: 8 }}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                Setup guide
                                            </a>
                                        )}
                                    </span>
                                }
                                name="applicationParamsSecrets"
                                style={{ marginBottom: 24 }}
                                rules={profileType === "app_deployment_(BYOC)" ? [
                                    {
                                        required: true,
                                        validator: () => {
                                            const validParams = parameters.filter(p => p.name.trim() && p.value.trim());
                                            const validSecrets = secrets.filter(s => s.name.trim() && s.value.trim());

                                            if (validParams.length === 0 || validSecrets.length === 0) {
                                                return Promise.reject(new Error("Please configure Client id and Client secret for BYOC deployment"));
                                            }
                                            return Promise.resolve();
                                        },
                                    },
                                ] : []}
                            >
                                <div>
                                    <Button
                                        type="link"
                                        onClick={() => {
                                            setParametersModalVisible(true);
                                            // Fetch current parameters/secrets in edit mode
                                            if (mode !== "edit") {
                                                const defaults = initializeParametersAndSecrets(profileType);
                                                if (parameters.length === 0) setParameters(defaults.parameters);
                                                if (secrets.length === 0) setSecrets(defaults.secrets);
                                            }
                                        }}
                                        disabled={isNotSpaceOwner || isSubmitting}
                                    >
                                        {mode === "edit" ? "Edit Parameters and Secrets" : "Configure Parameters and Secrets"}
                                        {profileType === "app_deployment_(BYOC)" && (
                                            <span style={{ color: "red", marginLeft: 4 }}>(Required)</span>
                                        )}
                                    </Button>
                                    {(parameters.length > 0 || secrets.length > 0) && (
                                        <div style={{ marginTop: 8 }}>
                                            <Text type="secondary">
                                                {parameters.filter(p => p.name.trim() && p.value.trim()).length} parameter(s), {secrets.filter(s => s.name.trim() && s.value.trim()).length} secret(s) configured
                                            </Text>
                                        </div>
                                    )}
                                </div>
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* Database provision fields for DSE App + App Deployment (BYOC) */}
                    {projectType === "dse_app" && profileType === "app_deployment_(BYOC)" && (
                        <div style={{
                            border: "2px dashed #1890ff",
                            borderRadius: "8px",
                            padding: "16px",
                            marginBottom: "16px",
                            backgroundColor: "#f6f9ff",
                            position: "relative",
                        }}>
                            {/* Header label for the BYOC section */}
                            <div style={{
                                position: "absolute",
                                top: "-12px",
                                left: "16px",
                                backgroundColor: "#fff",
                                padding: "0 8px",
                                fontSize: "14px",
                                fontWeight: 600,
                                color: "#1890ff",
                            }}>
                                BYOC RDS Storage Configuration
                            </div>
                            <Row gutter={16}>
                                <Col span={24}>
                                    <Form.Item
                                        name="need_rds_storage"
                                        label={
                                            <span style={{ fontWeight: 500 }}>
                                                Do you want to provision RDS storage?
                                                {mode === "edit" && editData?.need_rds_storage === "yes" && (
                                                    <>
                                                        &nbsp;
                                                        <Tooltip title="RDS storage is already provisioned. You cannot change this setting back to 'No'." overlayInnerStyle={{ maxWidth: 300 }}>
                                                            <InfoCircleOutlined style={{ color: "#1890ff" }} />
                                                        </Tooltip>
                                                    </>
                                                )}
                                            </span>
                                        }
                                        style={{ marginBottom: 0 }}
                                    >
                                        <Radio.Group
                                            disabled={isNotSpaceOwner || isSubmitting}
                                            onChange={e => {
                                                // Default the type when storage is first requested so the
                                                // existing flow stays unchanged (Aurora PostgreSQL default).
                                                if (e.target.value === "yes" && !form.getFieldValue("rds_storage_type")) {
                                                    form.setFieldsValue({ rds_storage_type: RDS_TYPE_DEFAULT });
                                                }
                                            }}
                                        >
                                            <Radio value="yes">Yes</Radio>
                                            <Radio
                                                value="no"
                                                disabled={
                                                    mode === "edit" &&
                                                    editData?.need_rds_storage === "yes" &&
                                                    needRdsStorage === "yes"
                                                }
                                            >
                                                No
                                            </Radio>
                                        </Radio.Group>
                                    </Form.Item>
                                </Col>
                            </Row>
                            {/* Database type selection — shown only when RDS storage is requested.
                                Options are driven by `rdsTypeOptions`, so adding another type
                                in the future requires only a config change (no UI edits here). */}
                            {needRdsStorage === "yes" && (
                                <Row gutter={16} style={{ marginTop: 16 }}>
                                    <Col span={12}>
                                        <Form.Item
                                            name="rds_storage_type"
                                            label={
                                                <span style={{ fontWeight: 500 }}>
                                                    Database Type
                                                    {mode === "edit" && editData?.need_rds_storage === "yes" && (
                                                        <>
                                                            &nbsp;
                                                            <Tooltip title="The database type is already provisioned and cannot be changed." overlayInnerStyle={{ maxWidth: 300 }}>
                                                                <InfoCircleOutlined style={{ color: "#1890ff" }} />
                                                            </Tooltip>
                                                        </>
                                                    )}
                                                </span>
                                            }
                                            rules={[{ required: true, message: "Select a database type" }]}
                                            style={{ marginBottom: 0 }}
                                        >
                                            <Select
                                                placeholder="Select database type"
                                                style={{ width: "100%" }}
                                                disabled={
                                                    isNotSpaceOwner ||
                                                    isSubmitting ||
                                                    (mode === "edit" && editData?.need_rds_storage === "yes")
                                                }
                                            >
                                                {rdsTypeOptions.map(type => (
                                                    <Option key={type.value} value={type.value}>
                                                        {type.label}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                </Row>
                            )}
                        </div>
                    )}

                    <Row gutter={16}>

                        {/* Single row for Bucket Type */}
                        <Col span={12}>
                            <Form.Item
                                label={
                                    <span>
                                        Type of Space
                                        {bucketType === "gxp" && (
                                            <>
                                                &nbsp;
                                                <Tooltip title="Note: Upload option is disabled for GxP spaces" overlayInnerStyle={{ minWidth: 340, maxWidth: 500 }}>
                                                    <InfoCircleOutlined style={{ color: "#1890ff" }} />
                                                </Tooltip>
                                            </>
                                        )}
                                    </span>
                                }
                                name={"bucketType"}
                                rules={[{ required: true, message: "Select space type" }]}
                            >
                                <Radio.Group onChange={e => handleBucketTypeChange(e.target.value)} disabled={mode === "edit" || isNotSpaceOwner || isSubmitting}>
                                    <Radio value="gxp">GxP</Radio>
                                    <Radio value="nongxp">Non-GxP</Radio>
                                </Radio.Group>
                            </Form.Item>
                        </Col>

                        {/* File System field is offered only for Non-GxP spaces */}
                        {showCustomFileSystem && (
                        <Col span={12}>
                            <Form.Item
                                label={
                                    <span>
                                        Custom File System&nbsp;
                                        <Tooltip
                                            title={
                                                <div>
                                                    <div className="tooltip-content__title">High-performance shared storage for ML teams and large-scale training.</div>
                                                    <ul className="tooltip-content__list tooltip-content__list--spaced">
                                                        <li className="tooltip-content__list-item"><strong>EFS:</strong> Shared scripts and bioinformatics databases (e.g., MSA Search, ColabFold).</li>
                                                        <li className="tooltip-content__list-item"><strong>FSx for Lustre:</strong> LLM Distributed Training and ultra-fast access to millions of small files.</li>
                                                    </ul>
                                                    <div>Both are cost-sensitive; delete FSx post-training to avoid idle costs.</div>
                                                </div>
                                            }
                                            overlayInnerStyle={{ minWidth: 340, maxWidth: 500 }}
                                        >
                                            <InfoCircleOutlined className="drawer-info-icon--muted" />
                                        </Tooltip>
                                    </span>
                                }
                                name="fileSystem"
                                preserve={false}
                            >
                                <Radio.Group disabled={Boolean((mode === "edit" && editData?.file_system_type) || isNotSpaceOwner || isSubmitting || !isGitRepoNeeded)}>
                                    <Radio value="efs">EFS</Radio>
                                    <Radio value="fsx">FSx</Radio>
                                </Radio.Group>
                            </Form.Item>
                        </Col>
                        )}

                        {/* for needCloudGpuCompute */}
                        <Col span={12}>
                            {projectType === "analytics" && (profileType === "ml_model_eng" || profileType === "ml_feature_eng") && (
                                <>
                                    <Form.Item
                                        name="needCloudGpuCompute"
                                        label="Need Cloud GPU Compute"
                                        rules={[{ required: true, message: "Please select Yes or No." }]}
                                    >
                                        <Radio.Group
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                if (value === "yes") {
                                                    setGpuModalVisible(true);
                                                }
                                            }}
                                            disabled={isNotSpaceOwner || isSubmitting}
                                        >
                                            <Radio value="yes">Yes</Radio>
                                            <Radio value="no">No</Radio>
                                        </Radio.Group>
                                    </Form.Item>
                                    <Modal
                                        title="Enabled GPU Instances"
                                        open={gpuModalVisible}
                                        onOk={() => setGpuModalVisible(false)}
                                        onCancel={() => setGpuModalVisible(false)}
                                        width={880}
                                        footer={[
                                            <Button key="close" type="primary" onClick={() => setGpuModalVisible(false)}>
                                                Close
                                            </Button>,
                                        ]}
                                    >
                                        <p>{gpuModalContent}</p>
                                        <Table
                                            bordered
                                            size="middle"
                                            pagination={false}
                                            className="gpu-table"
                                            dataSource={gpuInstances.map((item, index) => ({
                                                key: index,
                                                ...item,
                                            }))}
                                            columns={[
                                                {
                                                    title: "Instance Type",
                                                    dataIndex: "instance",
                                                    key: "instance",
                                                },
                                                {
                                                    title: "Memory",
                                                    dataIndex: "memory",
                                                    key: "memory",
                                                },
                                                {
                                                    title: "GPU",
                                                    dataIndex: "gpu",
                                                    key: "gpu",
                                                },
                                                {
                                                    title: "Storage",
                                                    dataIndex: "storage",
                                                    key: "storage",
                                                },
                                                {
                                                    title: "Cost(Approximate)",
                                                    dataIndex: "cost",
                                                    key: "cost",
                                                },
                                            ]}
                                        />
                                    </Modal>
                                </>
                            )}
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="primaryDomain"
                                label="Primary Domain"
                                rules={[
                                    { required: true, message: "Please select domain." },
                                ]}
                            >
                                <Input placeholder="Domain" disabled />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="region"
                                label="Region"
                                rules={[
                                    { required: true, message: "Please select region before proceeding." },
                                    {
                                        validator: (_, value) =>
                                            profileType === "gen_ai_eng" && value === "ap-southeast-1"
                                                ? Promise.reject(new Error("Gen AI Eng is not available in ap-southeast-1"))
                                                : Promise.resolve(),
                                    },
                                ]}
                            >
                                <Select placeholder="Select region" showSearch disabled={mode === "edit" ? true : false || isSubmitting}>
                                    {regionsList.map(region => (
                                        <Option key={region} value={region}>
                                            {region}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>



                    {/* ID Compliance (id_compliance) — only for the External Data
                        Exchange domain. Editable on create and on edit. Required. */}
                    {isExtDataExchange && (
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="idCompliance"
                                    label="ID Compliance"
                                    rules={[{ required: true, message: "Please select ID Compliance." }]}
                                >
                                    <Select
                                        placeholder="Select ID Compliance"
                                        disabled={isNotSpaceOwner || isSubmitting}
                                    >
                                        {idComplianceOptions.map(option => (
                                            <Option key={option.value} value={option.value}>
                                                {option.label}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                    )}

                    {/* R&D Project ID / Study ID — hidden for External Data Exchange (CP);
                        those fields are also omitted from the create/edit payload. */}
                    {!isExtDataExchange && (
                        <Form.Item
                            label={
                                <span>
                                    {"Select R&D Project ID / Study ID"}&nbsp;
                                    <Tooltip
                                        title={
                                            <div>
                                                <div>Please select R&D Project ID or Study ID, multiple selection possible. Study ID will be automatically mapped to the corresponding R&D Project ID.</div>
                                                <br />
                                                {(ENVIRONMENT !== "qa" && ENVIRONMENT !== "prod") && (
                                                    <div><strong>Note:</strong> {commonProject.project_name} is selected by default and can only be removed by adding another selection.</div>
                                                )}
                                            </div>
                                        }
                                        overlayInnerStyle={{ maxWidth: 500 }}>
                                        <InfoCircleOutlined style={{ color: "#1890ff" }} />
                                    </Tooltip>
                                </span>
                            }
                            name="selection"
                            rules={[{ required: true, message: "Please select at least one item." }]}
                        >
                            {/* Display selected items as tags */}
                            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.selection !== curr.selection}>
                                {({ getFieldValue }) => {
                                    const selected = getFieldValue("selection") || [];
                                    return (
                                        <div>
                                            {(selectedItems ?? []).length === 0 && (
                                                <Typography.Text type="secondary">No items selected</Typography.Text>
                                            )}
                                            <Button type="link" onClick={() => setModalOpen(true)}
                                                disabled={isNotSpaceOwner || isSubmitting}
                                            >
                                                {(selectedItems ?? []).length > 0 ? "Edit Selection" : "Select Items"}
                                            </Button>
                                        </div>
                                    );
                                }}


                            </Form.Item>

                            {(selectedItems ?? []).length > 0 && selectedType && (
                                <div style={{ paddingTop: 16 }}>
                                    <h4>Selected Items</h4>
                                    <div style={{ height: "300px", overflowY: "auto", overflowX: "auto" }}>
                                        <Table<ItemType>
                                            dataSource={selectedItems}
                                            rowKey={(record) => {
                                                if (selectedType === "project") {
                                                    return (record as ProjectItem).project_id;
                                                }
                                                return (record as ClinicalStudyItem).study_id;
                                            }}
                                            pagination={false}
                                            columns={selectedColumns}
                                            scroll={{ y: 300 }}
                                        />
                                    </div>
                                </div>
                            )}
                        </Form.Item>
                    )}

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="classification"
                                label="Classification"
                                rules={[{ required: true, message: "Please select classification" }]}
                            >
                                <Select placeholder="Select classification" disabled={isNotSpaceOwner || isSubmitting}>
                                    {availableClassifications.map((classificationValue) => (
                                        <Option key={classificationValue} value={classificationValue}>
                                            {classificationValue}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="licensingObligation"
                                label="Licensing Obligation"
                                rules={[{ required: true, message: "Please select obligation" }]}
                            >
                                <Select placeholder="Select obligation" disabled={isNotSpaceOwner || isSubmitting}>
                                    {licensingObligation.map((obligation) => (
                                        <Option key={obligation.value} value={obligation.value}>
                                            {obligation.label}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item name="secondaryDomain" label="Secondary Domain">
                                <Select placeholder="Optional domain" showSearch allowClear mode="multiple" disabled={isNotSpaceOwner || isSubmitting}>
                                    {secondaryDomain
                                        .filter((domain) => domain !== selectedPrimaryDomain) // Exclude selected primary domain
                                        .map((domain) => (
                                            <Option key={domain} value={domain}>
                                                {domain}
                                            </Option>
                                        ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        {/* Retention Time Dropdown */}
                        <Col span={12}>
                            <Form.Item
                                name="retentionType"
                                label="Retention Type"
                                rules={[{ required: true, message: "Choose retention type" }]}
                            >
                                <Select placeholder="Choose retention period" onChange={handleRetentionTimeChange} disabled={isNotSpaceOwner || isSubmitting}>
                                    <Option value="period">Choose retention period</Option>
                                    <Option value="date">Choose custom date</Option>
                                    <Option value="experimentperiod">Experiment period</Option>
                                </Select>
                            </Form.Item>
                        </Col>

                        {/* Conditional Retention Term or DatePicker */}
                        <Col span={12}>
                            {retentionTimeType === "period" ? (
                                <Form.Item
                                    name="retentionTerm"
                                    label="Retention Term"
                                    rules={[{ required: true, message: "Select retention term" }]}
                                >
                                    <Select placeholder="Select retention term" disabled={isNotSpaceOwner || isSubmitting}>
                                        {retentionTerms.map(term => (
                                            <Option key={term.value} value={term.value}>
                                                {term.label}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            ) : retentionTimeType === "date" ? (
                                <Form.Item
                                    name="retentionDate"
                                    label="Retention Date"
                                    rules={[{ required: true, message: "Choose a date" }]}
                                >
                                    <DatePicker
                                        style={{ width: "100%" }}
                                        disabled={isNotSpaceOwner || isSubmitting}
                                        disabledDate={(current) => current && current.isBefore(new Date(), "day")}
                                    />
                                </Form.Item>
                            ) : retentionTimeType === "experimentperiod" ? (
                                <Form.Item
                                    name="retentionTerm"
                                    label="Retention Term"
                                    rules={[{ required: true, message: "Select retention term" }]}
                                >
                                    <Select placeholder="Select retention term">
                                        {retentionTermsExtended.map(term => (
                                            <Option key={term.value} value={term.value}>
                                                {term.label}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            ) : null}
                        </Col>
                    </Row>

                    {/* Data Source */}
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                name="dataSource"
                                label="Data Source"
                            // rules={[{ required: true, message: "Enter data source" }]}
                            >
                                <Input placeholder="Enter data source" autoComplete="off" inputMode="text" disabled={isNotSpaceOwner || isSubmitting} />
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* Description */}
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item shouldUpdate={(prev, curr) => prev.description !== curr.description}>
                                {() => (
                                    <Form.Item
                                        name="description"
                                        label="Description"
                                        help={
                                            form.getFieldValue("description") &&
                                                form.getFieldValue("description").length >= 2000
                                                ? <span style={{ color: "#faad14" }}>2000 characters limit reached</span>
                                                : null
                                        }
                                        rules={[
                                            {
                                                max: 2000,
                                                message: "Description cannot exceed 2000 characters",
                                            },
                                        ]}
                                    >
                                        <Input.TextArea
                                            rows={4}
                                            placeholder="Enter description"
                                            disabled={isNotSpaceOwner || isSubmitting}
                                            maxLength={2000}
                                            showCount
                                        />
                                    </Form.Item>
                                )}
                            </Form.Item>
                        </Col>
                    </Row>

                    {mode === "edit" && (
                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item label="Registration Date" name="registrationDate">
                                    <Input disabled />
                                </Form.Item>
                            </Col>
                            {/* Conditional render if lastUpdatedBy is available */}
                            {
                                editData?.["last-modified-by"] && (
                                    <Col span={8}>
                                        <Form.Item label="Last Updated By" name="lastUpdatedBy">
                                            <Input disabled value={editData["last-modified-by"]} />
                                        </Form.Item>
                                    </Col>
                                )
                            }
                            {/* Conditional render if lastUpdatedOn is available */}
                            {
                                editData?.["last-update"] && (
                                    <Col span={8}>
                                        <Form.Item label="Last Updated On" name="lastUpdatedOn">
                                            <Input disabled value={editData["last-update"]} />
                                        </Form.Item>
                                    </Col>
                                )
                            }
                        </Row>
                    )}

                    {/* Owners Section */}
                    <Form.Item
                        name="owners"
                        rules={[
                            {
                                validator: (_, owners) => {

                                    const nonEmpty = Array.isArray(owners)
                                        ? owners.filter(({ user_email }: { user_email: string }) => user_email !== "")
                                        : [];

                                    if (nonEmpty.length >= 2) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(
                                        new Error("Please provide at least two owners."),
                                    );
                                },
                            },
                        ]}
                    >
                        {/* Header row: label and Add Owner button */}
                        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                            <Col>
                                <Text strong>
                                    Owners{""}
                                </Text>
                            </Col>
                            <Col>
                                <Button type="dashed" onClick={addOwner} icon={<PlusOutlined />} disabled={isNotSpaceOwner || isSubmitting}>
                                    Add Owner
                                </Button>
                            </Col>
                        </Row>

                        {/* Column header for entries */}
                        {owners.length > 0 && (
                            <Row gutter={16} style={{ marginBottom: 8, fontWeight: "bold" }}>
                                <Col span={20}>
                                    <Text>Owner Email {" "}
                                        <Tooltip title="Requestor will automatically obtain owner permission for the space">
                                            <InfoCircleOutlined style={{ color: "#1890ff" }} />
                                        </Tooltip>
                                    </Text>
                                </Col>
                                <Col span={4} />
                            </Row>
                        )}

                        {/* Owner entries */}
                        {owners.map((owner, index) => (
                            <Row gutter={16} key={`owner-${index}`} style={{ marginBottom: 8 }}>
                                <Col span={20}>
                                    <Select
                                        showSearch
                                        placeholder="Owner Email"
                                        filterOption={false}
                                        onSearch={(value) => debouncedOwnerSearch(value, "owner")}
                                        // notFoundContent={fetchingOwners ? <Spin size="small" /> : null}
                                        style={{ width: "100%" }}
                                        value={owner.user_email}
                                        disabled={isNotSpaceOwner || isSubmitting}
                                        onChange={(value) => {

                                            const updated = [...owners];
                                            updated[index] = { user_email: value, access_level: "owner" };
                                            setOwners(updated);
                                            setOwnerOptions([]); // Clear options after selection
                                        }}
                                    >
                                        {ownerOptions.map((opt) => (
                                            <Option key={opt.value} value={opt.email}>
                                                {opt.label}
                                            </Option>
                                        ))}
                                    </Select>
                                </Col>
                                <Col span={4}>
                                    {index !== 0 && (
                                        <Button
                                            danger
                                            icon={<MinusCircleOutlined />}
                                            onClick={() => removeOwner(index)}
                                            disabled={isNotSpaceOwner || isSubmitting}
                                        />
                                    )}
                                </Col>
                            </Row>
                        ))}
                    </Form.Item>

                    {/* Groups (Dynamic Group Input) */}
                    <Form.Item
                        name="groups"
                    // rules={[
                    //     {
                    //         validator: (_, groups) => {
                    //             const nonEmpty = groups.filter(({ access_level, group_name }: { group_name: string, access_level: string }) => (group_name !== "" && access_level !== "")) || [];

                    //             if (Array.isArray(nonEmpty) && nonEmpty.length >= 1 && nonEmpty.length == groups.length) {
                    //                 return Promise.resolve();
                    //             } else if (nonEmpty.length !== groups.length) {
                    //                 return Promise.reject(
                    //                     new Error("Please provide group details"),
                    //                 );
                    //             }
                    //             return Promise.reject(
                    //                 new Error("Please provide at-least one group."),
                    //             );
                    //         },
                    //     },
                    // ]}
                    >
                        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                            <Col>
                                <Text strong>
                                    Groups
                                    {bucketType?.toLowerCase() === "gxp" && trainingIdsForDomain.length > 0 && (
                                        <>
                                            &nbsp;
                                            <Tooltip
                                                title={
                                                    <div>
                                                        Only entitlement groups associated with Training ID(s):
                                                        <strong> {trainingIdsForDomain.join(", ")}</strong> will be available for selection
                                                    </div>
                                                }
                                                placement="right"
                                                overlayInnerStyle={{ maxWidth: 400 }}
                                            >
                                                <InfoCircleOutlined
                                                    className="drawer-info-icon"
                                                    aria-label="Training IDs information"
                                                />
                                            </Tooltip>
                                        </>
                                    )}
                                </Text>
                            </Col>
                            <Col>
                                <Button type="dashed" onClick={addGroup} icon={<PlusOutlined />} disabled={isNotSpaceOwner || isSubmitting}>
                                    Add Group
                                </Button>
                            </Col>
                        </Row>

                        {groups.length > 0 && (
                            <Row gutter={16} style={{ marginBottom: 8, fontWeight: "bold" }}>
                                <Col span={16}>
                                    <Text>Group Name</Text>
                                </Col>
                                <Col span={8}>
                                    <Text>Access Level</Text>
                                </Col>
                            </Row>
                        )}

                        {groups.map((group, index) => (
                            <Row gutter={16} key={`group-${index}`} style={{ marginBottom: 8 }}>
                                <Col span={12}>
                                    <Select
                                        showSearch
                                        placeholder="Group Name"
                                        filterOption={false}
                                        onSearch={debouncedSearch}
                                        loading={loadingTrainingGroups}
                                        notFoundContent={loadingTrainingGroups ? <Spin size="small" /> : null}
                                        style={{ width: "100%" }}
                                        value={group.group_name}
                                        onChange={(value) => {
                                            const updated = [...groups];
                                            updated[index].group_name = value;
                                            setGroups(updated);
                                            setGroupOptions([]);
                                        }}
                                        disabled={isNotSpaceOwner || isSubmitting}
                                    >
                                        {groupOptions.map((opt) => (
                                            <Option key={opt.value} value={`${opt.label} (${opt.value})`}>
                                                {`${opt.label} (${opt.value})`}
                                            </Option>
                                        ))}
                                    </Select>
                                </Col>
                                <Col span={8}>
                                    <Select
                                        placeholder="Access Level"
                                        value={group.access_level}
                                        style={{ width: "100%" }}
                                        onChange={(value) => {
                                            const updated = [...groups];
                                            updated[index].access_level = value;
                                            setGroups(updated);
                                        }}
                                        disabled={isNotSpaceOwner || isSubmitting}
                                    >
                                        <Option value="ro">Read Only</Option>
                                        <Option value="rw">Read and Write</Option>
                                        {needRdsStorage === "yes" && (
                                            <Option value="db_ro">DB Access Read Only</Option>
                                        )}
                                    </Select>
                                </Col>
                                <Col span={4}>
                                    {index !== 0 && (<Button danger icon={<MinusCircleOutlined />} onClick={() => removeGroup(index)} disabled={isNotSpaceOwner || isSubmitting} />)}
                                </Col>
                            </Row>
                        ))}
                    </Form.Item>
                    <SelectionModal
                        isModalOpen={modalOpen}
                        handleCancel={() => setModalOpen(false)}
                        onApplySelection={handleSelection}
                        projects={projects}
                        clinicalStudy={clinicalStudy}
                        selectedItemsProp={selectedItems}
                        selectionTypeProp={selectedType}
                    />



                </Form>
            )}

            {/* Parameters and Secrets Modal */}
            <Modal
                title="Parameters and Secrets"
                open={parametersModalVisible}
                onCancel={() => {
                    if (mode === "create") {
                        // Reset to empty state in create mode
                        setParameters([{ name: "", value: "" }]);
                        setSecrets([{ name: "", value: "" }]);
                    } else {
                        // In edit mode, fetch fresh API data without preserving local changes
                        fetchEditParametersSecrets(false);
                    }
                    setParametersModalVisible(false);
                }}
                width={900}
                centered
                bodyStyle={{
                    height: 500,
                    padding: "20px",
                    overflow: "hidden",
                }}
                footer={[
                    <Button
                        key="cancel"
                        onClick={() => {
                            if (mode === "create") {
                                // Reset to empty state in create mode
                                setParameters([{ name: "", value: "" }]);
                                setSecrets([{ name: "", value: "" }]);
                            } else {
                                // In edit mode, fetch fresh API data without preserving local changes
                                fetchEditParametersSecrets(false);
                            }
                            setParametersModalVisible(false);
                        }}
                    >
                        Cancel
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        loading={loadingParametersSecrets}
                        onClick={() => {
                            if (mode === "edit") {
                                handleUpdateParametersSecretsLocalState();
                                return;
                            }

                            // Create mode logic
                            const validParams = parameters.filter(p => p.name.trim() && p.value.trim());
                            const validSecrets = secrets.filter(s => s.name.trim() && s.value.trim());

                            // Check if there are rows with partial data (name but no value, or vice versa)
                            const partialParam = parameters.find(p =>
                                (p.name.trim() && !p.value.trim()) || (!p.name.trim() && p.value.trim()),
                            );
                            const partialSecret = secrets.find(s =>
                                (s.name.trim() && !s.value.trim()) || (!s.name.trim() && s.value.trim()),
                            );

                            if (partialParam || partialSecret) {
                                message.error("Please fill both name and value for all entries, or leave both empty");
                                return;
                            }

                            // Require at least one parameter and one secret
                            if (validParams.length === 0 || validSecrets.length === 0) {
                                message.error("Please add at least one parameter and one secret");
                                return;
                            }

                            // Update with only valid entries
                            setParameters(validParams);
                            setSecrets(validSecrets);

                            setParametersModalVisible(false);
                            message.success("Parameters and secrets saved successfully");
                        }}
                    >
                        {mode === "edit" ? "Update" : "Add"}
                    </Button>,
                ]}
            >
                <Spin spinning={loadingParametersSecrets} tip="Loading parameters and secrets...">
                    <div style={{ height: "100%", display: "flex", flexDirection: "column", gap: 16 }}>
                        {/* Parameters Table */}
                        <div style={{ flex: 1, minHeight: 0 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                                <Text strong style={{ fontSize: 16 }}>Parameters</Text>
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={() => setParameters([...parameters, { name: "", value: "" }])}
                                >
                                    Add
                                </Button>
                            </div>

                            <div style={{ height: "calc(100% - 48px)", border: "1px solid #d9d9d9", borderRadius: 6, overflow: "auto" }}>
                                <Table
                                    dataSource={loadingParametersSecrets ? [] : parameters.map((param, index) => ({ ...param, key: index }))}
                                    pagination={false}
                                    size="small"
                                    scroll={{ y: 200, scrollToFirstRowOnChange: false }}
                                    style={{ height: "100%" }}
                                    locale={{
                                        emptyText: loadingParametersSecrets
                                            ? "Loading..."
                                            : "No parameters added. Click 'Add' to create one.",
                                    }}
                                    columns={[
                                        {
                                            title: "Name",
                                            dataIndex: "name",
                                            key: "name",
                                            width: mode === "edit" ? "25%" : "40%",
                                            render: (text, record, index) => (
                                                <Input
                                                    value={text}
                                                    placeholder="Parameter name"
                                                    onChange={(e) => {
                                                        const updated = [...parameters];
                                                        updated[index].name = e.target.value;
                                                        setParameters(updated);
                                                    }}
                                                />
                                            ),
                                        },
                                        {
                                            title: "Value",
                                            dataIndex: "value",
                                            key: "value",
                                            width: mode === "edit" ? "25%" : "45%",
                                            render: (text, record, index) => (
                                                <Input
                                                    value={text || ""}
                                                    placeholder="Parameter value"
                                                    onChange={(e) => {
                                                        const updated = [...parameters];
                                                        updated[index].value = e.target.value;
                                                        setParameters(updated);
                                                    }}
                                                />
                                            ),
                                        },
                                        ...(mode === "edit" ? [{
                                            title: "Path",
                                            dataIndex: "path",
                                            key: "path",
                                            width: "35%",
                                            render: (text: any, record: any, index: number) => (
                                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                    <Input
                                                        value={text || ""}
                                                        placeholder="Parameter path"
                                                        readOnly
                                                        disabled
                                                        style={{ flex: 1 }}
                                                    />
                                                    {text && (
                                                        <Button
                                                            type="text"
                                                            icon={<CopyOutlined className="copy-icon" />}
                                                            size="small"
                                                            onClick={() => {
                                                                navigator.clipboard.writeText(text);
                                                                message.success("Path copied to clipboard");
                                                            }}
                                                            title="Copy path"
                                                        />
                                                    )}
                                                </div>
                                            ),
                                        }] : []),
                                        {
                                            title: "Actions",
                                            key: "actions",
                                            width: "15%",
                                            render: (_, record, index) => (
                                                <Button
                                                    danger
                                                    icon={<MinusCircleOutlined />}
                                                    onClick={() => {
                                                        if (parameters.length === 1) {
                                                            message.warning("At least one parameter is required");
                                                            return;
                                                        }
                                                        Modal.confirm({
                                                            title: "Delete Parameter",
                                                            content: `Are you sure you want to delete the parameter "${parameters[index].name}"?`,
                                                            okText: "Delete",
                                                            cancelText: "Cancel",
                                                            okButtonProps: { danger: true },
                                                            onOk: () => {
                                                                setParameters(parameters.filter((_, i) => i !== index));
                                                                message.success("Parameter deleted");
                                                            },
                                                        });
                                                    }}
                                                />
                                            ),
                                        },
                                    ]}
                                />
                            </div>
                        </div>

                        {/* Secrets Table */}
                        <div style={{ flex: 1, minHeight: 0 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                                <Text strong style={{ fontSize: 16 }}>Secrets</Text>
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={() => setSecrets([...secrets, { name: "", value: "" }])}
                                >
                                    Add
                                </Button>
                            </div>

                            <div style={{ height: "calc(100% - 48px)", border: "1px solid #d9d9d9", borderRadius: 6, overflow: "auto" }}>
                                <Table
                                    dataSource={loadingParametersSecrets ? [] : secrets.map((secret, index) => ({ ...secret, key: index }))}
                                    pagination={false}
                                    size="small"
                                    scroll={{ y: 200, scrollToFirstRowOnChange: false }}
                                    style={{ height: "100%" }}
                                    locale={{
                                        emptyText: loadingParametersSecrets
                                            ? "Loading..."
                                            : "No secrets added. Click 'Add' to create one.",
                                    }}
                                    columns={[
                                        {
                                            title: "Name",
                                            dataIndex: "name",
                                            key: "name",
                                            width: mode === "edit" ? "25%" : "40%",
                                            render: (text, record, index) => (
                                                <Input
                                                    value={text}
                                                    placeholder="Secret name"
                                                    onChange={(e) => {
                                                        const updated = [...secrets];
                                                        updated[index].name = e.target.value;
                                                        setSecrets(updated);
                                                    }}
                                                />
                                            ),
                                        },
                                        {
                                            title: "Value",
                                            dataIndex: "value",
                                            key: "value",
                                            width: mode === "edit" ? "25%" : "45%",
                                            render: (text, record, index) => (
                                                <Input.Password
                                                    value={text || ""}
                                                    placeholder="Secret value"
                                                    onChange={(e) => {
                                                        const updated = [...secrets];
                                                        updated[index].value = e.target.value;
                                                        setSecrets(updated);
                                                    }}
                                                />
                                            ),
                                        },
                                        ...(mode === "edit" ? [{
                                            title: "ARN",
                                            dataIndex: "arn",
                                            key: "arn",
                                            width: "35%",
                                            render: (text: any, record: any, index: number) => (
                                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                    <Input
                                                        value={text || ""}
                                                        placeholder="Secret ARN"
                                                        readOnly
                                                        disabled
                                                        style={{ flex: 1 }}
                                                    />
                                                    {text && (
                                                        <Button
                                                            type="text"
                                                            icon={<CopyOutlined className="copy-icon" />}
                                                            size="small"
                                                            onClick={() => {
                                                                navigator.clipboard.writeText(text);
                                                                message.success("ARN copied to clipboard");
                                                            }}
                                                            title="Copy ARN"
                                                        />
                                                    )}
                                                </div>
                                            ),
                                        }] : []),
                                        {
                                            title: "Actions",
                                            key: "actions",
                                            width: "15%",
                                            render: (_, record, index) => (
                                                <Button
                                                    danger
                                                    icon={<MinusCircleOutlined />}
                                                    onClick={() => {
                                                        if (secrets.length === 1) {
                                                            message.warning("At least one secret is required");
                                                            return;
                                                        }
                                                        Modal.confirm({
                                                            title: "Delete Secret",
                                                            content: `Are you sure you want to delete the secret "${secrets[index].name}"?`,
                                                            okText: "Delete",
                                                            cancelText: "Cancel",
                                                            okButtonProps: { danger: true },
                                                            onOk: () => {
                                                                setSecrets(secrets.filter((_, i) => i !== index));
                                                                message.success("Secret deleted");
                                                            },
                                                        });
                                                    }}
                                                />
                                            ),
                                        },
                                    ]}
                                />
                            </div>
                        </div>
                    </div>
                </Spin>
            </Modal>

            <Modal
                title="GxP Validation Warning"
                open={showGxpWarningModal}
                onOk={handleGxpWarningConfirm}
                onCancel={handleGxpWarningCancel}
                okText="Proceed"
                cancelText="Cancel"
                width={600}
            >
                <p>
                    No GxP process has been defined and validated for this domain yet. It is therefore not compliant to use this domain for GxP data.
                </p>
                {/* <p>
                    You can find more information on GxP use cases in DSE <a href="#" target="_blank" rel="noopener noreferrer">here</a>
                </p> */}
                <p>
                    <strong>Do you want to proceed with GxP space type for this domain?</strong>
                </p>
            </Modal>
        </Drawer>
    );
};

export default DrawerComponent;
